/**
 * Typed endpoint surface. Shapes were verified by calling the live backend, not read
 * off a spec — see docs/ARCHITECTURE.md D6 for why this is hand-written.
 *
 * Only the Phase 1 subset is here (docs/ARCHITECTURE.md §10). Adding an endpoint means
 * adding it here, never calling `fetch` from a component.
 */
import { api, ApiError, setCsrfToken } from './http'
import { formatBytes } from '../ui/bytes'
import { httpUrl } from './origin'
import {
  dynamoGetProjects,
  dynamoGetProjectHistory,
  dynamoCreateProject,
  dynamoSendProjectMessage,
  dynamoSendAreaQuestion,
  dynamoProjectMarkRead,
  dynamoGetProjectMembers,
  dynamoProjectInvite,
  dynamoProjectLeave,
  dynamoAttachProjectAgent,
  dynamoDetachProjectAgent,
  dynamoProjectAgentMode,
  dynamoGetAttachableAgents,
  dynamoProjectFileUpload,
  dynamoAttachmentBytes,
} from '../chat/projectDynamo'

/* ── agents ───────────────────────────────────────────────────────────────── */

export type AgentRow = {
  session: string
  /**
   * The paired machine this agent runs on, and what that machine calls itself.
   *
   * Absent on the raw `/agent/all` answer — a machine does not know its own id in
   * the account, and it should not: the id is the ACCOUNT'S name for it. Stamped
   * on by `getAgentsEverywhere`, which is the only place that knows both halves.
   *
   * Optional so the shape a single machine returns is still this type. `undefined`
   * means "whichever machine the relay is pointed at", which was the only answer
   * there was until the account had two.
   */
  device_id?: string
  device_name?: string
  /** null = this host; otherwise the mounted remote's name, e.g. "yy-mbp-home". */
  host: string | null
  /** Channel hash — also the seed for the agent's colour (identity/agentColor). */
  hash: string
  /**
   * INFORMATIONAL ONLY (backend 7802694). It means "we never pushed the README to this
   * session ourselves" — NOT "this agent doesn't know the protocol", because the guide
   * can arrive any way (the user can paste it). Cards no longer depend on it: the
   * registry mirrors DETECTION, so any agent, however created, gets one. Don't gate UI
   * on this.
   */
  onboarded: boolean
  /**
   * Has this agent read `avengents readme`?
   *
   * SEPARATE FROM `onboarded`, which means "this edge created this session" and is
   * written at creation — so every agent the app makes is onboarded and has read
   * nothing. Gating the `@` picker on `onboarded` would open it for exactly the agents
   * the gate exists to catch.
   *
   * Optional: an older edge does not send it. Absent is treated as "has read", not as
   * "has not" — a missing field must not make the picker unusable against an edge that
   * simply predates this.
   */
  read_guide?: boolean
  /**
   * WHICH guide `read_guide` was judged against — the same value on every row from
   * one machine, because it is the hash of the guide that machine carries.
   *
   * Only one thing uses it: scoping a person's "ignore" to a guide version, so that
   * dismissing today's prompt does not also dismiss the next guide (agents/guidePrompt).
   * Optional — an older edge does not send it, and `guideIgnored` answers "not
   * ignored" when it is absent rather than letting one dismissal swallow every
   * future guide.
   */
  guide_hash?: string
  /**
   * Epoch seconds when the agent's OWN edge last asked it to read the guide — a fork's
   * boot line does that (agentFork.ts). While that ask is fresh the arrival prompt
   * holds its tongue (agents/guidePrompt.ts): asking the person to send the same
   * instruction the machine just sent was the owner's 2026-09-13 complaint. Absent
   * from an edge that never asked, or one too old to say.
   */
  guide_asked_at?: number
  /**
   * The agent's edge brings the guide ITSELF — owner, 2026-09-29: the first `@` an
   * unread agent gets carries the readme line, and every CLI on that machine has the
   * `avengents` skill. `true` turns off this app's arrival prompt and `@` gate for the
   * row (agents/guidePrompt.ts). Absent from an edge too old to do either, which keeps
   * both prompts exactly as they were.
   */
  guide_on_ping?: boolean
  /** The session's working directory, as tmux reports it. The server has always sent
   *  this; the type dropped it, so "create another agent next to this one" had no
   *  context to work from and every new session defaulted to home. */
  cwd?: string | null
}

/** A tmux session with NO detected agent — the survivor of a quit CLI, or a plain
 *  shell. Listed so nothing invisible can own a name: the owner quit an agent, the
 *  roster (agents only) dropped the row, and the still-living session then blocked
 *  re-creating the name with nothing on screen to explain or remove it. */
export type TerminalRow = {
  session: string
  /** Same stamp as AgentRow's, applied by getAgentsEverywhere — the machine cannot
   *  know the account's name for it. Absent = "whichever machine the relay points at",
   *  which stopped being an acceptable answer the day the account had two machines. */
  device_id?: string
  device_name?: string
  host: string | null
  cwd?: string | null
  /** The pane's foreground program (`bash` = idle shell, `vim` = something running). */
  command?: string | null
  /** The folder as a person reads it, home as `~` (an edge from 2026-10-18 on). */
  path?: string
  /** Made by ＋ → Terminal: closed with terminalClose, and its stream ends with it. */
  plain?: boolean
}

export const getAgents = () =>
  api.get<{ agents: AgentRow[]; terminals?: TerminalRow[] }>('/agent/all')

/* Paired machines + unpair live in relay.ts — the one file allowed to speak
   to the BFF's /account routes without the edge's CSRF token (that service
   checks Origin and its own session cookie; see csrfCoverage.test.ts). */
export { getAccountDevices, unpairDevice, cancelUnpair, setDeviceNickname, type AccountDevice, type AccountAgent } from './relay'

/**
 * The FLEET as identity: every agent on the account, hash → (session, machine).
 * /agent/all is deliberately local (it is the sidebar); this one exists so the
 * Clean View can NAME a cross-machine sender instead of showing its hash.
 * ok:false with empty rows when the cloud is away — identity decoration
 * degrades, it never errors.
 */
export type FleetIdentityRow = { session: string; hash: string; machine: string }
export const getFleetIdentity = () =>
  api.get<{ ok: boolean; machine?: string; agents: FleetIdentityRow[] }>('/agent/fleet')

/**
 * Who holds a hash the fleet does NOT list — a sender from another account, the
 * exact case the message-blocking UI exists for. The edge asks the cloud
 * (`avengents check`'s lookup) and degrades to ok:false, so an unknown hash or
 * an unreachable cloud renders as the bare hash it always was, never an error.
 * `owner_*` is present only when the cloud has seen that account's browser
 * lately — absent means "nobody has looked", not "no owner".
 */
export type AgentCheckMatch = {
  session: string
  machine: string
  owner_name?: string
  owner_email?: string
}
export const getAgentCheck = (hash: string) =>
  api.get<{ ok: boolean; matches: AgentCheckMatch[] }>(
    `/agent/check?hash=${encodeURIComponent(hash)}`,
  )

/**
 * Which agents have this word in their recent terminal output.
 *
 * The edge matches the session name first, then greps each session's tmux
 * scrollback, and fans out to every mounted remote ITSELF — so the caller must NOT
 * pass `?host=`. Verified against the live server before building on it: `vault`
 * returns four sessions with no "vault" in any name, and nonsense returns zero
 * rather than everything.
 *
 * THE LIMITS MATTER MORE THAN THE FEATURE, because the UI must not imply more than
 * this does (stated by avengents-server, and the shape above is consistent with it):
 *   - the last ~30,000 lines of scrollback per session, NOT full transcript history
 *   - terminal output only — project chat and DMs live in other stores entirely
 *   - it returns WHICH sessions matched, never the matching line
 *   - cached ~3s per session, so typing does not re-grep on every keystroke
 */
export const agentSearch = (q: string) =>
  api.get<{ q: string; matches: { session: string; host: string | null; hash: string }[] }>(
    `/agent/search?q=${encodeURIComponent(q)}`,
  )

/** End a session completely: tmux session, pane process, transcript pin. The one
 *  action whose stated intent is to end whatever is running — the UI confirms first. */
/**
 * End an agent. `device` NAMES THE MACHINE, and it is not optional in practice.
 *
 * Without it the kill goes to whichever machine the relay happens to point at — and
 * the rail lists agents from EVERY paired machine, so the card for an agent on yy-a
 * opens while the relay is still aimed at aowl. The row's `host` cannot rescue that:
 * `/agent/all` is asked of each machine separately (agents/everywhere.ts), so a
 * remote row carries `device_id` and a `host` that is null — its own machine's word
 * for "local". aowl would receive a bare `{session}` and look for that name among ITS
 * sessions.
 *
 * That is not hypothetical here. `web-train-board` exists on two machines on this
 * account today, so the request that finds nothing on one of them is the lucky case;
 * the unlucky one ends the wrong agent, on the wrong machine, and reports success.
 *
 * agents/everywhere.ts already carries a comment about the identical bug in the
 * TERMINAL path — "carried no device, and clicking it left the relay pointed wherever
 * it already was … the owner got a plain shell in the wrong machine's home directory".
 * Same fault, one door along.
 */
/**
 * RESTART AN AGENT, KEEPING ITS CONVERSATION — owner, 2026-09-26: 讓上面增加一個按鈕,
 * 可以 restart 這個 Agent, 然後上面註解 restart 可以用來 update 它的 CLI working environment.
 *
 * `device` is REQUIRED for the same reason agentKill's is, and the reason is not
 * theoretical: /agent/all is asked of every paired machine separately, so a card opened
 * for an agent on one machine can be sitting there while the relay still points at
 * another. This account has had the same agent NAME on two machines. A restart that
 * landed on the wrong one would stop a working agent and report success — the identical
 * shape as the kill bug documented above it.
 *
 * The edge answers 409 for a refusal rather than 500, because a refusal is not a fault:
 * the agent is still running and nothing was lost. The caller needs those apart to say
 * "could not" instead of "did not".
 */
export const agentRestart = (
  session: string,
  host: string | null,
  device: string | null | undefined,
) =>
  api.post<{ ok: boolean; session: string; hash?: string; kind?: string; cwd?: string; detail?: string }>(
    `/agent/restart${host ? `?host=${encodeURIComponent(host)}` : ''}`,
    { session },
    device ?? undefined,
  )

/**
 * AN AGENT'S MODEL AND EFFORT — owner, 2026-09-26: 讓使用者可以看到它的 model 以及 Effort
 * 並且可以直接使用指令來更改.
 *
 * The edge reads it from the agent's own record and lists only what that CLI itself
 * offers; a change is remembered on the agent and applied by a restart with the CLI's
 * own launch flags (agentModel.ts on the edge carries why). `efforts` is null for the
 * kinds whose model id already carries the effort (agy, cursor).
 *
 * `device` is REQUIRED for the reason agentRestart's is: the POST restarts the agent,
 * and a card can be open for one machine while the relay points at another.
 */
export type ModelChoice = { id: string; label: string; efforts?: string[] }
export type AgentModelInfo = {
  ok: boolean
  session: string
  kind: string | null
  supported: boolean
  /**
   * What the RUNNING process uses — never the previous process's last reply (the edge's
   * preferRunning). `choice` is which offered model id that is, so the selectors can
   * start where the agent actually is.
   */
  current?: { model: string | null; effort: string | null; source: string; choice?: string | null }
  chosen?: { model: string | null; effort: string | null }
  choices?: { models: ModelChoice[]; efforts: string[] | null }
}

export const agentModel = (session: string, host: string | null, device: string | null | undefined) =>
  api.get<AgentModelInfo>(
    `/agent/model?session=${encodeURIComponent(session)}${host ? `&host=${encodeURIComponent(host)}` : ''}`,
    device ?? undefined,
  )

/**
 * BACK AN AGENT UP — owner, 2026-09-29: 「Backup this agent」. The MACHINE does the work:
 * it packs the agent (conversation, sidechains, memory, settings, notes), encrypts it to
 * the vault's PUBLIC key and uploads it straight to S3, so neither this app nor the BFF
 * ever holds a byte of it in the clear (edge agentBackup.ts).
 *
 * Answers at once with a job; progress arrives as `backup_progress` edge events, with
 * agentBackupStatus as the floor for a client that missed them. A second press while
 * one runs returns that job (`already`), never a second backup.
 *
 * `device` is REQUIRED for the reason agentRestart's is: a card can be open for an
 * agent on one machine while the relay points at another.
 */
export type BackupPhase = 'queued' | 'packing' | 'uploading' | 'committing' | 'done' | 'failed'
export type BackupJob = {
  job_id: string
  session: string
  kind: string
  phase: BackupPhase
  started_at: number
  finished_at: number | null
  /** Bytes read while packing; parts sent while uploading. */
  done: number
  total: number
  plain_size: number
  packed_size: number
  parts: number
  backup_id: string | null
  /** How many of the agent's older backups the cloud deleted to keep its newest five. */
  pruned: number
  error: string | null
  message: string | null
}

export const agentBackup = (session: string, device: string) =>
  api.post<{ ok: boolean; job: BackupJob; already?: boolean }>('/agent/backup', { session }, device)

/**
 * RESTORE A BACKUP ON A MACHINE — owner, 2026-09-29: 選擇他想要複製過去的機器 … folder …
 * 重新修改複製過去 Agent 的名稱. The machine makes a one-time key (prepare); this device
 * seals the backup's key to it; the machine fetches, opens and places the backup and
 * starts the agent (edge agentRestore.ts). Progress: `restore_progress` edge events.
 */
export type RestorePhase = 'queued' | 'fetching' | 'downloading' | 'unpacking' | 'starting' | 'done' | 'failed'
export type RestoreJob = {
  job_id: string
  backup_id: string
  session: string
  hash: string
  cwd: string
  kind: string
  from: string
  phase: RestorePhase
  started_at: number
  finished_at: number | null
  done: number
  total: number
  files: number
  kept: number
  notes_skipped: boolean
  error: string | null
  message: string | null
}

export const agentRestorePrepare = (device: string) =>
  api.post<{ ok: boolean; restore_id: string; epk: string }>('/agent/restore/prepare', {}, device)

export const agentRestore = (
  device: string,
  body: {
    restore_id: string
    backup_id: string
    sealed_dek: { eph: string; iv: string; ct: string }
    session: string
    cwd: string
    from: string
  },
) => api.post<{ ok: boolean; job: RestoreJob }>('/agent/restore', body, device)

export const agentRestoreStatus = (device: string, jobId?: string) =>
  api.get<{ jobs?: RestoreJob[]; job?: RestoreJob }>(
    `/agent/restore/status${jobId ? `?job=${encodeURIComponent(jobId)}` : ''}`,
    device,
  )

export const agentBackupStatus = (device: string, jobId?: string) =>
  api.get<{ jobs?: BackupJob[]; job?: BackupJob }>(
    `/agent/backup/status${jobId ? `?job=${encodeURIComponent(jobId)}` : ''}`,
    device,
  )

export const agentModelSet = (
  session: string,
  host: string | null,
  device: string | null | undefined,
  change: { model?: string | null; effort?: string | null },
) =>
  api.post<{
    ok: boolean
    session: string
    kind?: string
    chosen?: { model: string | null; effort: string | null }
    /** The agent restarted, but its CLI kept the old setting (codex, busy conversation). */
    notApplied?: { running: { model: string | null; effort: string | null }; detail: string }
  }>(
    `/agent/model${host ? `?host=${encodeURIComponent(host)}` : ''}`,
    { session, ...change },
    device ?? undefined,
  )

export const agentKill = (
  session: string,
  host: string | null,
  /*
   * REQUIRED, and that is the whole point — avg-devenv, 2026-08-22: 「那個函式的
   * `device` 是選填的，所以少傳不會被任何東西發現。如果它是必填，這一類就不會再有
   * 第三扇門。」
   *
   * It was optional, and this exact fault opened three doors in a row: the agent path,
   * everywhere.ts's terminal stamp, and the sidebar's delete. Each one was a caller
   * that simply did not pass it, and nothing anywhere could notice. `undefined` is
   * still a legal answer — it means "wherever the relay points", which is right for a
   * row that genuinely has no machine — but a caller now has to write it down.
   */
  device: string | null | undefined,
  /*
   * ALSO DELETE THE CLI'S OWN CONVERSATION — owner, 2026-08-23: delete agent session
   * 的時候應該也要考慮把 claude / codex / cursor / antigravity 的舊的 session 完整的
   * 刪除。對吧。
   *
   * Required for the same reason `device` above is: the edge has carried this since
   * a5adc6f and NOTHING sent it, so the feature existed and was unreachable — proven
   * by the owner's own probe1, whose 19KB conversation was still on disk after the
   * agent was deleted. An optional flag would have been the same silence.
   *
   * The edge deletes only what it can PROVE belongs to this session and refuses the
   * rest with a reason: a claude conversation is taken only via the pin, and a
   * directory-identified CLI (codex/cursor/agy) only when no other agent shares that
   * directory. So `true` here cannot reach a neighbour's history.
   */
  purge: boolean,
) =>
  api.post<{ ok: boolean; killed: string; purged?: unknown[] }>(
    `/agent/kill${host ? `?host=${encodeURIComponent(host)}` : ''}`,
    { session, purge },
    // `api.post` takes `string | undefined`; null and undefined mean the same thing to
    // it — "no machine named, use the relay target". The parameter above accepts both
    // so a caller can say "none" out loud instead of by omission.
    device ?? undefined,
  )

/**
 * ＋ → TERMINAL — owner, 2026-10-18: 純粹的一個 tmux Terminal, at the machine's home folder, with
 * no agent in it (the edge's terminalSessions.ts). The machine names the session; an edge
 * older than the route answers 404, which terminalErrorText reads as "needs an update".
 */
export const terminalCreate = (device: string) =>
  api.post<{ ok: boolean; session: string; cwd: string; path: string }>('/terminal/create', { where: 'home' }, device)

/** End a ＋ → Terminal session — the session only; the edge refuses anything it did not mark. */
export const terminalClose = (session: string, device: string | null | undefined) =>
  api.post<{ ok: boolean; ended?: boolean; already_gone?: boolean }>('/terminal/close', { session }, device ?? undefined)

/** Rename an agent's session. The hash derives from the name, so this reissues
 *  the agent's channel address — the menu warns about it. Returns the new name. */
export const renameAgent = (session: string, newName: string, host?: string | null) =>
  api.post<{ ok?: boolean; error?: string; message?: string; session?: string }>(
    '/agent/rename',
    { session, new_name: newName, ...(host ? { host } : {}) },
  )

/** Fork a Claude conversation into a NEW session at a chosen folder — Claude's
 *  own `--fork-session`, so the original is untouched. Returns the new name.
 *
 *  `device` NAMES THE MACHINE THE SOURCE LIVES ON. Without it this rode the
 *  ambient relay target — and a same-machine fork aimed at tbc-m1 landed on
 *  whichever machine the relay happened to point at, where the chosen folder's
 *  home does not exist, so every local fork died as `cwd_unwritable` while the
 *  named machine was never asked (owner, 2026-08-25; proved by forking the same
 *  path against each machine directly). The same wrong-machine family as
 *  agentKill's `device` above. */
export const forkAgent = (
  session: string,
  opts: { newName: string; cwd: string; host?: string | null; device?: string | null },
) =>
  api.post<{ ok?: boolean; error?: string; message?: string; detail?: string; session?: string }>(
    '/agent/fork',
    { session, new_name: opts.newName, cwd: opts.cwd, ...(opts.host ? { host: opts.host } : {}) },
    opts.device ?? undefined,
  )

/**
 * CLONE AN AGENT INTO A CHOSEN TYPE — owner, 2026-09-30: any agent's conversation, carried
 * into a new Claude, Codex or Grok agent on the same machine (edge agentClone.ts). Same-type
 * Claude/Codex use the CLI's own fork; anything else is rebuilt from the messages.
 */
export const CLONE_TYPES = ['claude', 'codex', 'grok'] as const
export type CloneType = (typeof CLONE_TYPES)[number]
export const agentClone = (
  device: string,
  body: { session: string; target: CloneType; new_name: string; cwd: string },
) =>
  api.post<{ ok?: boolean; session?: string; kind?: string; method?: 'native_fork' | 'rebuilt'; omitted?: number; error?: string; message?: string }>(
    '/agent/clone',
    body,
    device,
  )

/* ── vault / identity ─────────────────────────────────────────────────────── */

export type SecurityStatus = {
  ok: boolean
  configured: boolean
  needs_setup: boolean
  enforcement: boolean
  registered: boolean
  email_verified: boolean
  /** False ⇒ /project/* and /chat/* return `vault_locked`, and SSE 401s. */
  unlocked: boolean
  username: string | null
  email: string | null
  device_id: string
  device_fp: string
  router_mode: string
  csrf: string
  /**
   * WHY the vault would refuse this client, when the server is new enough to say.
   *
   * Same three values as the 401's `reason`. Optional because it arrives with
   * avengents-server 3042ea7 and this build has to work against an edge without
   * it — which is the current one, deliberately not restarted while the owner is
   * using the app.
   */
  lock_reason?: string
  lock_message?: string
  session_expires_at: number | null
  group: {
    group_id: string
    name: string
    role: string
    admin_email: string
    expires_at: number
  } | null
  group_pending: unknown[]
  handshakes: unknown[]
  pending: unknown[]
}

/**
 * Is the vault OPEN for this deployment's purposes?
 *
 * needs_setup:true is the steady state here — the vault is deliberately never
 * created (owner, 2026-08-05: the vault lock is gone, Google is the gate), so a
 * vault that was never created does not lock anything. A vault that EXISTS and is
 * locked still locks. ONE spelling, exported, because the first fix patched App's
 * copy and missed the Sidebar's — same fact, two derivations, and the projects
 * rail stayed dark after creation had already been fixed (owner: 可以創建但是
 * 看不到卡片).
 */
export const vaultOpen = (
  v: { unlocked?: boolean; needs_setup?: boolean; configured?: boolean } | null | undefined,
): boolean =>
  // configured:false is the robust spelling of "never created" — needs_setup
  // additionally depends on the server's WT_VAULT_REQUIRED flag, and this must
  // not go dark because that flag flipped.
  v?.unlocked === true || v?.needs_setup === true || v?.configured === false

/**
 * Also captures the CSRF token as a side effect. Every mutating /security/* call needs
 * it, and this is the only endpoint that hands it out — so binding the two together
 * here means no call site has to remember. Status is polled anyway.
 *
 * `unlocked` is scoped to THIS CLIENT, not to the server. wtserver/security.py forces
 * it to false unless the caller presents the live `wt_sec` cookie, so "unlocked:false"
 * means "this browser holds no unlock session", NOT "the vault is locked". Two
 * consequences that have each cost a debugging session:
 *   - `wt_sec` is Secure+SameSite=Strict, so over plain HTTP the browser DISCARDS it:
 *     unlock returns 200 and nothing works afterwards. The dev server serves HTTPS for
 *     exactly this reason (vite.config.ts).
 *   - Any tool checking status must keep a cookie jar, or it will conclude the vault
 *     is locked while another client is happily using it.
 */
export const getSecurityStatus = async (): Promise<SecurityStatus> => {
  const s = await api.get<SecurityStatus>('/security/status')
  setCsrfToken(s.csrf ?? null)
  return s
}

/*
 * THE VAULT WRITE PATH IS GONE — setupVault, unlockVault, lockVault,
 * changeVaultPassword, passwordError and the two length constants, deleted 2026-09-02.
 *
 * They served src/security/VaultGate.tsx and VaultSetup.tsx, which rendered nowhere:
 * Google sign-in replaced that box. The READ path stays and is live — getSecurityStatus
 * and vaultOpen above — because the vault still gates agent transcripts on the machine,
 * and every screen that says "unlock to see this" reads it.
 */

/* ── projects (channels) ──────────────────────────────────────────────────── */

export type ProjectRow = {
  project_id: string
  name?: string
  /**
   * Messages in this project the owner has not read: authored by someone else and
   * newer than their cursor. Optional because an older edge does not send it, and a
   * missing count must render as no badge rather than as zero unread.
   *
   * NOT the `unread` boolean on individual rows in the server's message table. That
   * flag marks a message as having arrived by relay from a DIFFERENT owner's edge,
   * which never happens in a single-owner deployment — it is a permanent, correct
   * zero, and counting it would produce a badge that is right about the wrong
   * question and so never gets reported as broken.
   */
  unread?: number
  /**
   * The newest message in the room, WHOEVER wrote it — the owner's own included.
   *
   * Deliberately the opposite rule to `unread` above, on the same rows of the same
   * table: `unread` asks "is there anything here for me" and excludes the owner,
   * this asks "what happened in this room last" and excludes nobody. avengents-server
   * named their test `test_it_is_the_newest_message_whoever_wrote_it` for the same
   * reason this comment exists — the pair is easy to collapse into one rule, and the
   * card shows both at once, so collapsing them would make it disagree with itself.
   *
   * Framing (`<project-msg:…>`) is stripped and the text is capped server-side; an
   * undecryptable body is omitted rather than sent as an empty string.
   */
  last_msg_preview?: string | null
  /**
   * Channel hash of the author, or null when a human wrote it.
   *
   * Null, never a placeholder: this seeds the PixelPet, and a made-up hash would draw
   * a confident face for someone who has none.
   */
  last_from_hash?: string | null
  /** Readable author — an agent's label or a person's email. */
  last_from?: string | null
  /**
   * A HUMAN author's address and photo, for their face on the card (owner,
   * 2026-09-27: 預覽圖並沒有顯示人類的頭像). Absent for an agent, and absent from a
   * backend too old to send them — both keep the neutral dot.
   */
  last_from_email?: string | null
  last_from_picture?: string | null
  /**
   * The room's newest message seq (DynamoDB path only; the edge list does not send it).
   * Marking the card read advances the read cursor to this — see {@link projectMarkRead}.
   */
  last_seq?: number
  /**
   * Set only on a friend DM standing in as a room: the peer's email.
   *
   * A DM and a project are the same thing to almost everything downstream — a
   * conversation with an id, a title, an unread count and a page of messages — so the
   * Activity feed renders both from this one shape. What differs is what TAPPING a row
   * opens: a project by id, a DM by peer. This field is that difference, declared
   * rather than sniffed, so a reader can find it.
   */
  dm_peer?: string
  /**
   * Set only on a GROUP's chat room (owner, 2026-10-18): the group it belongs to. The room follows its
   * group — people come and go with the group — and it is listed under that group in Humans, not with
   * the projects.
   */
  group_id?: string
  [k: string]: unknown
}

export const getProjects = () =>
  dynamoGetProjects()
/**
 * Advance a project's read cursor. Server-owned, like {@link agentMarkRead} — the
 * two halves of the sidebar must not obey different rules about what "read" means
 * when the owner reads on the phone and looks at the desktop.
 *
 * The server advances to the timestamp of the project's newest message rather than
 * to wall-clock now, so a message that lands mid-flight is not marked read by a
 * clock the reader cannot see.
 */
export const projectMarkRead = (projectId: string, seq?: number) =>
  dynamoProjectMarkRead(projectId, seq)
/** Create a project. The name is the only required field; the id comes back. */
export const createProject = (name: string) =>
  dynamoCreateProject(name)
export type ProjectMessage = {
  message_id: string
  /** Monotonic, NOT dense. See transport/sse.ts reconcile(). */
  cursor: number
  ts: number
  body: string
  from_ref?: { email?: string; agent?: string; label?: string; picture?: string }
  [k: string]: unknown
}

/**
 * `limit` on its own means THE NEWEST N — the server orders by seq DESC, takes the
 * page, then sorts it back into display order (wtserver/project.py project_history).
 * That is what makes an activity feed affordable: the alternative is every room's
 * whole history on every open, which is the cost Clean View has just finished paying
 * off on the transcript side.
 *
 * With `after` it means something different — the next N of the gap, oldest first —
 * so the two are not interchangeable and this signature keeps them adjacent on
 * purpose. Junk values are ignored server-side rather than rejected, degrading to
 * the whole history: safe, but a caller that silently loses its bound would just be
 * slow, not wrong, and slow is what we came here to fix.
 */
export const getProjectHistory = (projectId: string, after?: number, limit?: number) =>
  dynamoGetProjectHistory(projectId, after, limit)
export const sendProjectMessage = (
  projectId: string,
  message: string,
  fromEmail?: string | null,
  fromName?: string | null,
  fromPicture?: string | null,
  replyTo?: number,
  board?: { id: string; name: string },
  page?: { id: string; name: string },
) =>
  dynamoSendProjectMessage(projectId, message, fromEmail, fromName, fromPicture, replyTo, board, page)
/** A question about an area of a whiteboard, with its picture (board/area.ts). Resolves with the row as sent. */
export const sendAreaQuestion = dynamoSendAreaQuestion
/* ── group members (trust domain people) ──────────────────────────────────── */

/**
 * Shape is authoritative per docs/AGENTS-SIDEBAR-GROUP-NESTING.md — the edge passes
 * the router's `group.members` through verbatim, and only `active` members come back.
 *
 * `chat_key` is the DM-ability gate: present ALL-or-NONE. Absent ⇒ this member has no
 * registered chat key, so a DM would be dead. Do not offer one.
 */
export type GroupMember = {
  email: string
  device_id: string
  public_key: string
  role: 'admin' | 'member'
  /** creator = 1, ascending. Sort key and owner marker. */
  join_seq: number
  receive_url: string | null
  chat_key?: {
    chat_public_key: string
    key_epoch: number
    kid: string
    expires_at: number
  }
  chat_receive_url?: string | null
}

export const getGroupMembers = () =>
  api.get<{ ok: boolean; members: GroupMember[] }>('/security/groups/members')

/* ── new agent session (the +New sheet) ───────────────────────────────────── */

/*
 * THE LIVE WIRE SHAPE, replacing a v1 relic. The old type here claimed top-level
 * {claude, cursor, codex, agy} with install/bootstrap one-liners — a shape no
 * deployed edge has answered since the rewrite, and the reader that "worked"
 * against it was reading undefined (mock-the-wire lesson, again: the first
 * cliPresence cut tested green against this type and did nothing against the
 * real edge). The edge answers {ok, clis: {<key>: …}}; `path`/`via`/`searched`
 * arrive from b51410a's exhaustive detection and are optional so older edges
 * still parse.
 */
export type CliInfo = {
  installed: boolean
  label: string
  /** Absolute path the edge resolved, when found. */
  path?: string
  /** Which search found it: the service's launch PATH, or the user's login shell. */
  via?: 'launch-path' | 'login-shell'
  /** The command this machine runs for "Install for me" (POST /agent/cli_install). Absent:
   *  no installer for this CLI there, or an edge from before 2026-10-16. */
  install?: string
  /** What that button installs there, one row each (owner, 2026-10-26: 讓使用者知道按下按鈕之後
   *  哪些東西會被安裝) — Hermes brings llama.cpp and Ollama. Absent on an older edge. */
  install_includes?: InstallItem[]
}

/** One thing "Install for me" installs: its name and one short line. */
export type InstallItem = { key: string; label: string; detail: string }

export type CliStatus = {
  ok: boolean
  clis: Record<string, CliInfo>
  /** The service PATH the edge searched — what a not-found can show. */
  searched?: string
}

/**
 * What every host is doing.
 *
 * Three states, and the third is the one worth having: a host in `mounted` is
 * connected, one in `detached` was disconnected ON PURPOSE and the server's
 * self-healer is leaving it alone, and one in NEITHER list is being retried and
 * failing. Before `detached` existed the last two were indistinguishable, so a
 * machine the owner had switched off looked exactly like one that was refusing to
 * connect. Added by avengents-server at my request.
 */
export const getRemoteStatus = () =>
  api.get<{ mounted: { host: string; name: string }[]; detached?: string[] }>('/remote/status')

/**
 * Every host this machine COULD mount — the aliases in its ssh config.
 *
 * The universe to choose from. /remote/connect rejects anything not in here with
 * `unknown host`, so this is the list rather than free text.
 */
export const getSshHosts = () => api.get<{ hosts: string[] }>('/ssh/hosts')

/**
 * Mount a host: ssh install if needed, then a tunnel. Idempotent, and it leaves
 * other mounts alone. Slow the first time — it is a real connection, not a flag.
 */
export const remoteConnect = (host: string) =>
  api.post<{ ok?: boolean }>('/remote/connect', { host })

/** Tear down ONE host's mount. Omitting the host would drop them all, so never do. */
export const remoteDisconnect = (host: string) =>
  api.post<{ ok?: boolean }>('/remote/disconnect', { host })


/* ── agent conversation (transcript view + send) ──────────────────────────── */

export type TranscriptBlock = {
  /**
   * `thinking` is reasoning the agent did, NOT anything it said, and the two must
   * not render alike — an undeclared kind fell through to the assistant branch and
   * appeared with the agent's avatar and an "agent" badge, turning private
   * deliberation into a quotation.
   *
   * EVERY source can emit it, from four separate places in the server
   * (`agent_api.py` 2542 codex reasoning summary · 2610 agy PLANNER_RESPONSE · 2682
   * cursor narration before a tool call · 2692 claude thinking content block). I
   * first recorded this as Antigravity-only because a live count showed 5 for agy
   * and 0 for the others — but claude only emits it with extended thinking on, and
   * codex only when that turn had a reasoning summary, so the window I sampled was
   * silent rather than the sources being. **Counted-none is not cannot-happen**, and
   * that is the same sampling mistake that hid the bug this comment is about.
   */
  kind: 'assistant' | 'user' | 'tool_use' | 'tool_result' | 'thinking'
  /** Tool name, for kind === 'tool_use'. */
  name?: string
  text?: string
  error?: boolean
  /**
   * Path this tool call acted on, and whether `text` is a unified diff.
   *
   * Both were already on the wire and read by nothing here — 27 of avengents-server's
   * 277 blocks carry them. `diff` is a FLAG, not content: the diff itself is in
   * `text`, and the boolean says how to read it. Verified against a live block rather
   * than assumed from the name, which was worth doing — "diff" reads like it would
   * hold the patch, and a client that rendered it would have shown the word "true".
   *
   * The cost of ignoring them was quiet: a run of sixteen edits collapsed to "Edit"
   * with no indication of WHICH files, and a patch rendered as flat monospace with
   * its additions and removals the same colour.
   */
  file?: string
  diff?: boolean
  /**
   * When the record was WRITTEN — epoch seconds, UTC (avg-main's wire contract,
   * 2026-08-20, in answer to owner: 在 Clean View 裡顯示對話 Block 傳遞的時間點).
   * The model saying it, the tool returning — never the moment the browser received
   * it, which backfill would collapse to one meaningless instant.
   *
   * OPTIONAL, because two of the five transcript paths genuinely have no clock:
   * Antigravity's protobuf fallback carries none per record, and cursor-agent
   * stamps only the USER half (owner ruled that scope). Absent stays absent all the
   * way to the pixel — `blockTime.ts` explains why nothing is ever interpolated.
   */
  ts?: number
  /**
   * The CLI's own summary of the conversation before it — claude's compaction turn (`isCompactSummary`), marked by the
   * edge since 528011e (transcriptParse.ts). A `user` block nobody typed: Clean View folds it to one line (Msg's
   * CompactedNote). Absent from an older edge, which draws it as before.
   */
  summary?: boolean
}

export type Transcript = {
  ok: boolean
  /** Transcript file id. Changes ⇒ a different conversation, so state must reset. */
  file: string
  /** 'replace' = a window; 'append' = new blocks since `have`; 'backfill' = older ones. */
  mode: 'replace' | 'append' | 'backfill'
  /**
   * ABSOLUTE index of the first block in `blocks`, not an offset into what the client
   * holds. The two are only equal for a client that holds everything from the start,
   * which is why `have` below has to be computed from this rather than from an array
   * length.
   */
  from: number
  total: number
  truncated?: boolean
  /** How many older blocks exist below `from`. 0 ⇒ the window reaches the beginning. */
  pending?: number
  blocks: TranscriptBlock[]
  cwd?: string
}

/**
 * Transcript as a DELTA. `have` is an ABSOLUTE block index and the server only appends
 * when there is no gap — otherwise it falls back to `replace`.
 *
 * `file` is not optional in practice, even though the endpoint accepts its absence.
 * The append branch is `client_file == fname and base <= have <= total`, so a request
 * that omits it compares '' against the file token, never matches, and is answered
 * with the WHOLE transcript. This client omitted it and polled every 2 seconds, which
 * is how a conversation's length turned into a cost paid thirty times a minute rather
 * than once when it opened. The comment here used to say resume was correct by
 * construction; it was correct by construction and unreachable in practice.
 */
export type TranscriptSource = 'claude' | 'cursor' | 'codex' | 'agy' | 'grok' | 'hermes' | 'muse'

export const getTranscript = (
  session: string,
  have: number,
  host?: string | null,
  source: TranscriptSource = 'claude',
  tail?: number,
  file?: string | null,
) =>
  api.get<Transcript>(
    `/agent/transcript?session=${encodeURIComponent(session)}&have=${have}&source=${source}` +
      (tail ? `&tail=${tail}` : '') +
      (file ? `&file=${encodeURIComponent(file)}` : '') +
      (host ? `&host=${encodeURIComponent(host)}` : ''),
  )

/**
 * The page of history below `before` — the other half of `tail`.
 *
 * avengents-server's own note on why this exists: "A full replace can be a single
 * ~8 MB JSON frame (2000 blocks × 4 KB); a phone must paint the tail first, not parse
 * all of it." Both parameters have been on the endpoint the whole time; this client
 * simply asked for everything, every time it opened a conversation.
 */
export const getTranscriptBefore = (
  session: string,
  before: number,
  limit: number,
  host?: string | null,
  source: TranscriptSource = 'claude',
  file?: string | null,
) =>
  api.get<Transcript>(
    `/agent/transcript?session=${encodeURIComponent(session)}` +
      `&before=${before}&tail=${limit}&source=${source}` +
      /*
       * PIN THE FILE, like the poll one line of this module away already does.
       *
       * Without it the server resolves the newest transcript for the cwd, and
       * sibling sessions sharing a working tree resolve to the SAME newest .jsonl
       * — avengents-server's reason the idle check cannot read the transcript
       * either. A page fetched from another file fails the caller's file check and
       * is dropped, so scroll-back does nothing at all, forever, with no error and
       * `pending` still saying there is history to load: the exact shape of "it
       * stops". Checked against the live edge — same 120 blocks and same `from`
       * with and without, so pinning costs nothing and removes the ambiguity.
       */
      (file ? `&file=${encodeURIComponent(file)}` : '') +
      (host ? `&host=${encodeURIComponent(host)}` : ''),
  )

/**
 * DO NOT use this for the owner's own input from the UI.
 *
 * `/agent/message` is the agent-to-agent bus. It frames the text as an incoming *agent*
 * message, so a human composer — which has no channel hash to put in `from` — produces
 * `<agent-msg:unknown> … </agent-msg:unknown>`: unattributed, and with no reply
 * address. `?raw=1` only hides that symptom; the channel is still wrong.
 *
 * A real user's keystrokes belong in the PTY, over /ws — which is how the original UI
 * works and why it never produces agent-msg blocks for the owner's typing. See
 * transport/ws.ts.
 *
 * Kept for the legitimate case: relaying a message on behalf of another agent.
 */
export const sendAgentMessageAsAgent = (
  session: string,
  message: string,
  from: string,
  opts?: { host?: string | null; submit?: boolean },
) =>
  api.post<{ ok?: boolean; error?: string; delivered?: boolean }>(
    `/agent/message?session=${encodeURIComponent(session)}` +
      (opts?.host ? `&host=${encodeURIComponent(opts.host)}` : ''),
    { session, message, from, ...(opts?.submit === false ? { submit: false } : {}) },
  )

/**
 * The OWNER speaking to an agent — the one path that can be HELD.
 *
 * Typed text used to go straight into the pty over /ws, which was right about
 * attribution (a human's keystrokes are not an agent message, and framing them as one
 * produced unattributed `<agent-msg:unknown>` blocks) and structurally unable to obey
 * the other rule: while an agent is busy, everything addressed to it queues. The pty
 * has no queue — the characters simply land in whatever the CLI has on screen.
 *
 * I reported that rule as satisfied once. It was not: I had measured every path
 * through `inject()` and called it every path, and the owner's own typing is the one
 * that does not go through it. The owner found it by using the product.
 *
 * `/agent/say` is that missing path, and the three properties are why it exists rather
 * than reusing `/agent/message?raw=1`:
 *
 *   queues when blocked   the same queue and the same flush loop as every other
 *                         delivery, not a second mechanism that could disagree
 *   never framed          the text arrives as the owner wrote it
 *   never counts unread   you do not have unread mail from yourself; `raw=1` calls
 *                         `_record_inbound_framed` and would have given you some
 *
 * `queued: true` is a SUCCESS. The message is accepted and held; what the caller must
 * not do is tell the user it was seen.
 */
export const postAgentSay = (session: string, message: string, host?: string | null, device?: string | null) =>
  api.post<{
    ok?: boolean
    error?: string
    delivered?: boolean
    queued?: boolean
    depth?: number
    /** Why it is being held, in words meant for a person. */
    detail?: string
    // `device` names the machine the agent is on when that is not the one the relay is
    // pointed at — More › Chrome tabs speaks to whichever agent holds the tab.
  }>('/agent/say', { session, message, ...(host ? { host } : {}) }, device ?? undefined)

/**
 * The full text of ONE held message, fetched when someone opens it.
 *
 * Throws `ApiError(404)` when the id is no longer held — which is a normal race, not
 * a fault: a message can flush between the list arriving and the reader clicking. The
 * server answers `not_queued` and OMITS the body key rather than sending an empty
 * string, because "" would tell the reader this message had no content, which is a
 * different and confidently wrong statement.
 */
export const getQueuedBody = (id: string) =>
  api.get<{ ok?: boolean; id: string; body: string; from_label?: string | null }>(
    `/agent/queues?full=${encodeURIComponent(id)}`,
  )

/* ── file browser ─────────────────────────────────────────────────────────── */

export type FsKind = 'image' | 'audio' | 'video' | 'markdown' | 'text' | 'other'

export type FsItem = {
  name: string
  type: 'dir' | 'file'
  /** Files only; null when stat failed. */
  size?: number | null
  kind?: FsKind
}

export type FsListing = {
  path: string
  /** null at the filesystem root. */
  parent: string | null
  entries: FsItem[]
  /** Server caps listings at 2000 entries. */
  truncated: boolean
  /** The default path (an agent's cwd) no longer exists; the listing fell back
   *  to home and this names where it came from (owner, 2026-08-27, mega8). */
  fallback_from?: string
  error: string | null
}

/**
 * List a directory.
 *
 * Omit `path` and pass `session` to get the server's default: the ACTIVE TMUX PANE's
 * directory for that agent (wtserver/tmux_sessions.py `default_browse_path`), falling
 * back to the live shell's cwd then the server cwd. That is why the browser can open
 * where the agent actually is without the client guessing.
 */
export const fsListDir = (
  path?: string | null,
  session?: string | null,
  host?: string | null,
) => {
  const q = new URLSearchParams()
  if (path) q.set('path', path)
  if (session) q.set('session', session)
  // /fs/* is in the remote proxy middleware's path list: ?host= routes the request to
  // that mounted remote's edge, so browsing happens where the files actually live.
  if (host) q.set('host', host)
  return api.get<FsListing>(`/fs/list${q.toString() ? `?${q}` : ''}`)
}

/**
 * A file's content, as JSON, so it can cross the relay.
 *
 * `/fs/file` is a raw byte stream and this client cannot reach it: every call here is
 * an `http.request` command over the link, and that path is JSON-only — the edge does
 * `JSON.parse` on the response and falls back to `{error: <first 200 chars>}`. Asked
 * from the browser's own origin, `/fs/file` answers 404 `no_such_route`, and so do
 * `/note/file` and `/project/file`. So the viewer was not missing polish; no file
 * content of any kind could arrive.
 *
 * `encoding` is DECLARED by the edge rather than inferred here. Guessing would corrupt
 * an image whose first bytes happen to decode as text, which reads as a broken file
 * instead of a broken transport.
 */
export type FsContent = {
  ok: boolean
  name?: string
  size?: number
  kind?: FsKind
  mime?: string
  encoding?: 'utf8' | 'base64'
  data?: string
  truncated?: boolean
  /** 'too_large' when the file cannot cross the link; `limit` says by how much. */
  error?: string
  limit?: number
}

export const fsContent = (path: string, host?: string | null) =>
  api.get<FsContent>(
    `/fs/content?path=${encodeURIComponent(path)}` +
      (host ? `&host=${encodeURIComponent(host)}` : ''),
  )


/**
 * These three take FORM bodies, not JSON — the handlers use `request.post()` /
 * `request.multipart()`. Sending JSON would silently read as empty fields.
 */
/**
 * All three are host-routed. /fs/* is in the proxy path list, so without `host` a
 * mutation aimed at a mounted remote silently lands on Origin instead — the same
 * wrong-host class as the terminal attaching to a local pty.
 */
/*
 * JSON over the relay, not a form to the page origin — owner, 2026-08-08: File
 * Browser 现在没有办法删除档案 … 上面会出现问题. The form POST went to this
 * origin, which has no /fs proxy (deliberately — the edge is reached through the
 * relay), so the API-shape guard answered 404 no_such_route and the browser
 * showed it as the delete "having a problem". Reading worked all along because
 * fsContent already rode the relay. The edge's fields() has accepted JSON since
 * v1 compatibility, so only this side had to move. fsUpload stays multipart and
 * stays broken on this backbone until it gets the chunk lane note files use.
 */
/**
 * ARTIFACTS — a page or app on one machine, at a public URL.
 *
 * Owner-token gated on the edge (routes.ts, deliberately absent from
 * SELF_AUTHORIZING), reached the way every other edge route is: through the
 * relay, aimed at ONE named machine. `device` is not optional in spirit — an
 * artifact belongs to the machine that publishes it, and a share aimed at the
 * wrong machine would change the wrong thing.
 */
export async function artifactList(device: string) {
  return api.get<{
    ok: boolean
    items: Array<{
      hash: string
      kind: 'static' | 'port'
      summary: string
      name: string
      visibility: 'private' | 'friends' | 'public'
      url: string
      source: string
      live: boolean
      refused?: string
    }>
    link: { connected: boolean; base: string; detail: string; accepted?: boolean; paused?: string; terms_url?: string }
  }>('/artifact/list', device)
}

export async function artifactShare(
  device: string,
  hash: string,
  visibility: 'private' | 'friends' | 'public',
  /**
   * WHICH friends — owner, 2026-09-20. Three values, three meanings, and the
   * machine keeps them apart:
   *
   *   undefined  leave whatever list this artifact already had
   *   null       every friend, including anyone added later (the default)
   *   string[]   exactly these people; `[]` is nobody
   *
   * `null` exists because JSON cannot send `undefined`: without a word for
   * "back to everyone" the picker could narrow an audience and never widen it
   * again.
   */
  allow?: string[] | null,
) {
  return api.post<{ ok: boolean; item: { hash: string; visibility: string; url: string; allow?: string[] } }>(
    '/artifact/share',
    allow === undefined ? { hash, visibility } : { hash, visibility, allow },
    device,
  )
}

/**
 * TAKE AN ARTIFACT DOWN — owner, 2026-09-25: 我可以從 Artifact 的下拉選單來刪除那個
 * artifact, 也就是解除 Local 端與 BFF 端的連接.
 *
 * It severs both halves in one call, and the machine is the only place that can do it:
 * the item lives in ~/.avengents/artifacts.json on the publishing machine, and the
 * relay only ever learns about it from that machine's sync. So this posts to the EDGE
 * (via the relay tunnel, like every other artifact call) and the unregister follows on
 * the next sync — `forget: true`, which drops the live entry AND releases the claim, so
 * the hash is genuinely free rather than merely unserved.
 *
 * The account-level row (ARTOWNER#) is deliberately untouched: it maps an opaque owner
 * id to an account and is shared by ALL of that person's artifacts, so deleting one
 * artifact must not remove it.
 *
 * IRREVERSIBLE, which is the caller's problem to present: the URL does not come back.
 * Re-adding the same folder mints the same hash, but anything already shared stays dead
 * in between.
 */
export async function artifactRemove(device: string, hash: string) {
  return api.post<{ ok: boolean; item?: { hash: string; kind: string; source: string } }>(
    '/artifact/remove',
    { hash },
    device,
  )
}

export async function fsMkdir(dir: string, name: string, host?: string | null) {
  const q = host ? `?host=${encodeURIComponent(host)}` : ''
  return api.post<{ created?: string; error?: string }>(`/fs/mkdir${q}`, { dir, name })
}

export async function fsDelete(path: string, host?: string | null) {
  const q = host ? `?host=${encodeURIComponent(host)}` : ''
  return api.post<{ deleted?: string; error?: string }>(`/fs/delete${q}`, { path })
}

/**
 * Rename a file or folder in place — owner, 2026-09-14, relaying a user's ask for
 * rename in the browser's long-press menu.
 *
 * `to` is a NAME, not a path: the edge reduces it to its basename, so this cannot move
 * anything between folders however it is called. The edge refuses to rename onto a
 * name that is taken (409), for the same reason upload keeps both — a rename that ate
 * someone's file and answered ok is data loss reported as success.
 */
export async function fsRename(path: string, to: string, host?: string | null) {
  const q = host ? `?host=${encodeURIComponent(host)}` : ''
  try {
    return await api.post<{ renamed?: string; name?: string; error?: string }>(`/fs/rename${q}`, {
      path,
      to,
    })
  } catch (e) {
    if (laneMissing(e)) throw new Error(edgeTooOld('the rename lane'))
    throw e
  }
}

/** The multipart route's cap, mirrored (fsChunks.ts FS_UPLOAD_MAX). Checked before a
 *  single chunk moves, so a doomed upload is one readable sentence, not an hour. */
export const FS_UPLOAD_MAX = 5 * 1024 * 1024 * 1024

/** What "this machine cannot do that yet" actually means, said once for both
 *  directions of the lane. */
const FS_LANE_TOO_OLD =
  "this machine's avengents-server is older than the file-transfer lane — " +
  'it updates itself within a few minutes, or run `avengents update` on it'

const laneMissing = (e: unknown): boolean =>
  e instanceof ApiError && (e.body as { error?: string } | null)?.error === 'no_such_route'

/**
 * The same sentence as FS_LANE_TOO_OLD, for capabilities added after it.
 *
 * Every /fs capability that arrived as its OWN ROUTE — rename, replace-on-upload —
 * lands here when the machine has not updated yet, and that is by design: a new route
 * 404s on an old edge, which is a fact this side can SAY, where a new field on an old
 * route would have been silently ignored and reported as done.
 */
const edgeTooOld = (what: string): string =>
  `this machine's avengents-server is older than ${what} — ` +
  'it updates itself within a few minutes, or run `avengents update` on it'

/**
 * Upload over the relay, in 512 KiB slices — /fs finally has the lane notes and
 * project files got on 2026-08-07 (owner, 2026-09-02: 你給的建議都做).
 *
 * From 2026-08-07 to 2026-09-02 this was a multipart POST to the PAGE ORIGIN, which
 * has no /fs proxy, and every upload died at the API-shape guard; then an honest local
 * refusal while the edge lane was built. The refusal's ghost survives in one branch:
 * an edge too old to carry the lane answers `no_such_route`, and that is translated
 * back into a sentence about the MACHINE, because "route missing" reads as our bug
 * when the actual state is their edge has not updated yet.
 *
 * Files go SEQUENTIALLY, awaiting each chunk: the edge enforces exact-next `seq`
 * (parallel chunks would be refused, correctly), and two files at once through one
 * relay just split the same wire. Progress is bytes-across-everything, since the
 * person staged a batch, not a queue of separate transfers.
 */
export async function fsUpload(
  dir: string,
  files: File[],
  host?: string | null,
  onProgress?: (pct: number) => void,
  /**
   * Names the person chose to REPLACE rather than keep beside the existing file
   * (owner, 2026-09-14, relaying a user: 我其实是想覆盖而不是生成一个 A_1).
   *
   * A set of names, not a flag for the batch: only the files that actually collided
   * need the newer edge route, so dropping four files where one clashes still works
   * on a machine that has not updated — and the one that needs the update says so
   * instead of failing the whole drop.
   */
  overwrite?: ReadonlySet<string>,
) {
  const q = host ? `?host=${encodeURIComponent(host)}` : ''
  const total = files.reduce((n, f) => n + f.size, 0) || 1
  let sent = 0
  const saved: string[] = []
  for (const file of files) {
    if (file.size > FS_UPLOAD_MAX) {
      throw new Error(`${file.name} is larger than the ${formatBytes(FS_UPLOAD_MAX)} limit`)
    }
    let begin: { ok?: boolean; upload_id?: string; error?: string }
    try {
      begin = await api.post(`/fs/upload_begin${q}`, { dir, name: file.name, size: file.size })
    } catch (e) {
      if (laneMissing(e)) throw new Error(FS_LANE_TOO_OLD)
      throw e
    }
    if (!begin.upload_id) throw new Error(begin.error ?? 'upload refused')
    const id = begin.upload_id
    let seq = 0
    for (let o = 0; o < file.size; o += UPLOAD_CHUNK) {
      const buf = await file.slice(o, o + UPLOAD_CHUNK).arrayBuffer()
      await api.post(`/fs/upload_chunk${q}`, { upload_id: id, seq, data_b64: b64(buf) })
      seq += 1
      sent += buf.byteLength
      onProgress?.(Math.round(Math.min(100, (sent / total) * 100)))
    }
    /*
     * Two finish routes, and which one is called is the whole compatibility story.
     *
     * "Keep both" is the OLD route, unchanged, so nothing about the common path asks
     * anything new of a machine. "Replace" is a route an older edge does not have, so
     * it answers no_such_route and the person is told this machine needs an update —
     * rather than being told the file was replaced while that edge quietly wrote
     * "A (1)" next to it, which is what an `on_conflict` FIELD on the old route would
     * have produced on every machine that had not updated yet.
     */
    const replace = overwrite?.has(file.name) ?? false
    let fin: { ok?: boolean; saved?: string; error?: string }
    try {
      fin = await api.post(
        replace ? `/fs/upload_finish_as${q}` : `/fs/upload_finish${q}`,
        replace ? { upload_id: id, on_conflict: 'overwrite' } : { upload_id: id },
      )
    } catch (e) {
      if (replace && laneMissing(e)) throw new Error(edgeTooOld('replacing a file on upload'))
      throw e
    }
    if (!fin.saved) throw new Error(fin.error ?? `could not save ${file.name}`)
    saved.push(fin.saved)
    // A zero-byte file never enters the chunk loop; its progress lands here.
    onProgress?.(Math.round(Math.min(100, (sent / total) * 100)))
  }
  return { saved }
}

/**
 * Download over the same lane: page /fs/data forward until eof, hand back a Blob.
 *
 * The size from the FIRST slice is the contract for the rest — if the file grows or
 * shrinks mid-download the reassembly would be a chimera of two versions, so a
 * mismatched final size is an error, not a shrug. `/fs/file` next door streams with
 * Range support and stays the better road where raw bytes are reachable (native
 * shell); this one exists because a stream has no road over the relay.
 */
/** How many times in a row one slice may fail before the whole download does. */
const SLICE_RETRIES = 2

export async function fsDownload(
  path: string,
  host?: string | null,
  onProgress?: (pct: number) => void,
  /** `type` names the bytes for a reader that plays them (the viewer); `signal` stops
   *  the paging between slices when nobody is waiting for the file any more. */
  opts?: { type?: string | undefined; signal?: AbortSignal },
): Promise<Blob> {
  const hq = host ? `&host=${encodeURIComponent(host)}` : ''
  const parts: BlobPart[] = []
  let offset = 0
  let size: number | null = null
  let misses = 0
  for (;;) {
    if (opts?.signal?.aborted) throw new Error('stopped')
    let r: { ok?: boolean; data_b64?: string; size?: number; eof?: boolean; error?: string }
    try {
      r = await api.get(`/fs/data?path=${encodeURIComponent(path)}&offset=${offset}${hq}`)
      misses = 0
    } catch (e) {
      if (laneMissing(e)) throw new Error(FS_LANE_TOO_OLD)
      /* ONE SLOW SLICE IS NOT THE FILE FAILING. A hundred slices in a row is a hundred
         chances for a cell handoff; the same offset is asked again before forty
         megabytes already here are thrown away. A slice is a read — asking twice
         changes nothing on the machine. */
      if (opts?.signal?.aborted || ++misses > SLICE_RETRIES) throw e
      continue
    }
    /* Whoever was waiting has gone (the viewer closed, or moved to another file): no
       progress is reported for a file nobody is looking at. */
    if (opts?.signal?.aborted) throw new Error('stopped')
    if (!r.ok || typeof r.data_b64 !== 'string') throw new Error(r.error ?? 'download failed')
    size ??= r.size ?? null
    if (size !== null && r.size !== size) {
      throw new Error('the file changed while it was downloading — try again')
    }
    const bin = atob(r.data_b64)
    const bytes = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
    /* A Blob per slice, not the array: the final Blob then composes these by reference,
       where an array of fifty megabytes would be copied into it and held beside it. */
    parts.push(new Blob([bytes]))
    offset += bin.length
    if (size) onProgress?.(Math.round(Math.min(100, (offset / size) * 100)))
    if (r.eof) break
  }
  return new Blob(parts, opts?.type ? { type: opts.type } : undefined)
}

/**
 * The largest file the viewer fetches to SHOW (owner, 2026-10-11: 放寬到五十個 MB …
 * 瀏覽 image 或是看 video).
 *
 * `/fs/content` answers a file in ONE message and stops at 2.5 MB because that message
 * has a ceiling on every hop. Nothing on those hops was raised: past 2.5 MB the viewer
 * pages the same bytes through `fsDownload`'s slices, which have no ceiling of their
 * own. So this number is the viewer's patience and the phone's memory, not the
 * transport's — the file list's "Download file" still carries anything.
 */
export const FS_VIEW_MAX = 50_000_000

/* ── idle status + project membership ─────────────────────────────────────── */

/**
 * The edge's sentinel in a SENDER field for a message the OWNER sent — the UI
 * composer, a note handed to an agent, a fork's boot line (edge:
 * agentIdentity.OWNER_SENDER).
 *
 * IT IS NOT AN ADDRESS and no roster will ever resolve it, so every surface that
 * renders a sender has to know it by name or draw a stranger called
 * "__owner__" — which is what the agent transcript did until it was special-cased
 * there, and what the Activity feed still did afterwards, shortening it to
 * "__owner_" over the owner's own words. One constant so the third surface that
 * needs it cannot invent a different spelling.
 *
 * (The tests deliberately keep the literal: a test that reads this constant
 * cannot notice the constant itself being wrong.)
 */
export const OWNER_SENDER = '__owner__'

/** One row of GET /agent/idle_status — per-session activity, polled at 5s. */
export type IdleAgent = {
  host?: string | null
  session: string
  /**
   * WHICH MACHINE answered. Stamped by getIdleEverywhere, never sent on the wire —
   * a machine has no name for itself in this account's terms, and `host` cannot
   * stand in because every machine calls its own agents `host: null`.
   */
  device_id?: string
  device_name?: string
  /** Channel hash; the busy float and jump both key on it. */
  hash?: string
  /** 'active' = working right now. */
  status: string
  needs_input?: boolean
  unread?: number
  /**
   * The opening of what this agent last SAID, from its own transcript.
   *
   * The direction matters and I got it wrong first: `last_msg_preview` below is
   * what someone last said TO this agent, which is a real thing and not what the
   * rail wants. An agent that has been talking all day but received no
   * agent-to-agent message showed nothing at all.
   *
   * EXPECT MORE NULLS THAN FEELS RIGHT, and they are deliberate
   * (avengents-server 9273c89):
   *   - only when the transcript is MAPPED — a session pinned at /agent/create.
   *     A guessed transcript resolves to the newest .jsonl in the cwd, so two
   *     agents sharing a directory would each show the other's words under their
   *     own name.
   *   - Claude only. codex and cursor have no equivalent structured log, and the
   *     alternative was guessing from the TUI frame — which would have put the
   *     same "auto mode on" line under every agent, aligned and meaningless.
   *
   * So a blank line reads "nothing yet", and that is the point: a confident wrong
   * preview costs someone an afternoon, a blank one costs nothing.
   */
  last_said_preview?: string | null
  /**
   * WHY the preview is null, so a blank row can be read rather than guessed at.
   *
   *   ok                    there is text
   *   nothing_yet           mapped, nothing said
   *   transcript_ambiguous  more than one transcript recently written under this
   *                         cwd — the words cannot be attributed. TRANSIENT: the
   *                         window is 15 minutes, so a row can be ambiguous now and
   *                         fine shortly after.
   *   transcript_stale      the pinned file is gone
   *   no_transcript         nothing to read
   *   vault_locked          the gate removed it; nothing about the agent
   *
   * The last one exists because a reason that said `ok` beside a stripped preview
   * would have the rail explain a state the client is not in — the same one-string-
   * for-three-causes bug, committed while fixing it. avengents-server's addition.
   */
  last_said_reason?: string
  /**
   * The opening of the last message delivered TO this agent. Kept because it
   * answers a genuine question — who spoke to this agent — but it is NOT what the
   * rail shows, for the reason above.
   */
  last_msg_preview?: string | null
  /**
   * WHEN that message was delivered — epoch SECONDS (a float; `time.time()`).
   *
   * The only timestamp this endpoint carries, and the reason the Activity feed can
   * order agents at all. Null until the agent has received something. It used to
   * say the row resets on server restart; it does not — the counters persist, and
   * since 2026-09-21 so does the preview beside them (the edge's own note on
   * INBOUND_KEEP records why the old trade was withdrawn: the trim cost the line
   * on every read, not only across a restart).
   *
   * There is deliberately NO equivalent for `last_said_preview`: nothing on the wire
   * says when an agent last spoke, so nothing may place it on a timeline. See
   * activity/feed.ts.
   */
  last_msg_ts?: number | null
  /**
   * Channel hash of whoever sent that message, or null when nothing has arrived.
   *
   * The value was always there and always thrown away: `_record_inbound_framed`
   * matches `<agent-msg:HASH>` only to skip self-messages, read group(1), and
   * dropped it. Added by avengents-server at my request — before it, every bus row
   * in the Activity feed was about the RECEIVER with no way to name the other end.
   */
  last_msg_from?: string | null
  /**
   * The sender's NAME, when a PERSON wrote it — Project Chat and Friend Chat both.
   *
   * A person has no channel hash, so `last_msg_from` above is null for every
   * message typed by a human and the feed had nobody to name: it printed the
   * literal "someone" over the owner's own words until 2026-09-21. This field is
   * separate rather than `last_msg_from` being widened because readers treat that
   * one as a hash and resolve it against the roster — a name put there renders as
   * a truncated 8-character "hash" the moment the lookup misses.
   *
   * Both surfaces are covered by one field: the chat service feeds a room's agents
   * from a single loop that never branches on DM-vs-project.
   */
  last_msg_from_name?: string | null
  /** Cumulative agent-bus messages delivered to this session since the server started. */
  msg_count?: number
  /**
   * WHEN this agent last spoke — epoch seconds, from the transcript LINE's own ISO
   * timestamp, not the file's mtime.
   *
   * That distinction is the whole value of the field and it is avengents-server's
   * point, not mine: mtime moves when ANY line is appended, so a tool result or the
   * user's next turn would rewrite "when the agent last spoke" to a moment it did
   * not speak — close enough to look right, wrong with nothing to flag it. They pinned
   * it with a test.
   *
   * KEEPS FLOWING WHILE THE VAULT IS LOCKED, deliberately: the gate's line is
   * CONTENT, not whether something happened, and `last_msg_ts` was always ungated
   * the same way. So a locked client gets time-without-text rows — the text is
   * WITHHELD, not absent, and `last_said_reason` is what says which.
   */
  last_said_ts?: number | null
  /**
   * The opening of the last thing a PERSON typed at this agent — owner, 2026-09-13:
   * Agent 忙碌並且沒有吐出來新的 Message Block 的時候, 把使用者的最後一個 Message 放在
   * 訊息預覽裡面.
   *
   * The rail's second line is what the agent SAID, which is blank for the whole of a
   * long turn — exactly when someone wants to know what it is working on. This is the
   * other direction, read from the same transcript tail.
   *
   * NEVER AN ARRIVED MESSAGE. Another agent's message and a chat message both reach a
   * CLI as a user turn; the edge skips those, because the rail draws this line under
   * the account's OWN face and a stranger's words there would be a lie. Null when the
   * edge cannot attribute a line, and stripped by the vault gate exactly like
   * last_said_preview — a person's own words are no less private than the agent's.
   */
  last_ask_preview?: string | null
  /** WHEN that was typed — epoch seconds, from the transcript line's own timestamp.
   *  The rail compares it with last_said_ts to decide which direction is newer. */
  last_ask_ts?: number | null
  /** 'claude-code' | 'cursor' | 'screen' — drives the auto-detect view. */
  kind?: string
}

export const getIdleStatus = () => api.get<{ agents: IdleAgent[] }>('/agent/idle_status')

/**
 * The agent-bus event log — the last N messages delivered anywhere on this edge.
 *
 * A `deque(maxlen=200)` appended at the same delivery points that already feed the
 * per-session counters, so it costs nothing extra and, like them, is memory-only:
 * a restart empties it. That trade is deliberate on both sides — persisting message
 * content to disk for a feed is the thing avengents-server would refuse and I did
 * not ask for.
 *
 * `from` is resolved to a readable name SERVER-SIDE, and that is the right split:
 * the registry lives on the edge, so a client handed a bare hash could only draw the
 * hash — which is exactly what the readable-sender work removed from the terminal.
 * It is null when the hash cannot be resolved (an unmounted host, another edge), and
 * falling back to `from_hash` there is the caller's choice, not a server default.
 *
 * Shape verified against the live edge (74fc393) before anything was built on it.
 */
export type BusEvent = {
  /** Epoch seconds. */
  ts: number
  from_hash: string
  /** Readable sender, or null when this edge cannot resolve the hash. */
  from?: string | null
  to_host: string | null
  to_session: string
  /** Body opening, framing stripped, capped at PREVIEW_MAX_CHARS server-side. */
  preview?: string | null
}

export const getAgentActivity = (limit = 100) =>
  api.get<{ ok?: boolean; events: BusEvent[] }>(`/agent/activity?limit=${limit}`)

/**
 * Advance a session's read cursor. The SERVER owns it — no localStorage — so reading on
 * one device clears the badge on every device. In the proxy path list, so `host` routes
 * it to the edge where the cursor actually lives.
 */
export const agentMarkRead = (session: string, host?: string | null) => {
  const q = new URLSearchParams({ session })
  if (host) q.set('host', host)
  return api.post<{ ok?: boolean }>(`/agent/mark_read?${q}`)
}

/** What an attached agent may do in the room: hear + post, hear only, or neither. */
export type AgentMode = 'speak' | 'listen' | 'mute'

/** A project member: a human (email) or an attached agent (channel_hash + label). */
export type ProjectMember = {
  kind?: string
  email?: string
  /** Their picture: an uploaded avatar's path, else the sign-in provider's URL, else
   *  null. The chat service applies that precedence (friendBridge.profileOf); the
   *  BFF's profile.view is the other half of the same rule. Not "the Google photo"
   *  since 2026-09-13, when a person could upload their own. */
  picture?: string | null
  /** chat v1 user id of a HUMAN row — the same id an agent's `owner_user` names, so the
   *  roster can seat each agent under the person who attached it. */
  user?: string
  /** A HUMAN row's standing in the room: `owner` is the one whose room it is (board/EditLock). */
  role?: string
  /** A HUMAN row's friend-list `account` handle — which of two people at one address is here. */
  account?: string
  owner_email?: string
  /** chat v1 user id of the human who attached this AGENT (matches a human row's `user`). */
  owner_user?: string
  /** The owner's display name (PROFILE# name via the human rows), resolved where both
   *  halves are in hand — so a busy chip can say WHOSE agent, not which machine. */
  owner_label?: string
  channel_hash?: string
  /** A HUMAN's name (their chosen one, else the provider's — projectDynamo maps the
   *  service's `name` here) or an AGENT's label. One field, two kinds of row. */
  label?: string
  mode?: AgentMode
  /** Stamped by the edge: it attached this agent, so its controls will work. */
  mine?: boolean
  [k: string]: unknown
}

export const getProjectMembers = (projectId: string) =>
  dynamoGetProjectMembers(projectId)
export const projectInvite = (projectId: string, toEmail: string, account?: string) =>
  dynamoProjectInvite(projectId, toEmail, account)
/** Attachable coding-agent sessions (local + mounted remotes); already-attached ones
 *  are excluded when project_id is passed. */
export type AttachableAgent = {
  channel_hash: string
  label: string
  session: string
  host: string | null
  /** The hosting machine — present on the DynamoDB path (sourced from getAgentsEverywhere),
   *  which the cloud attach needs; the edge path omits it (it resolves device server-side). */
  device_id?: string
  /**
   * What that machine is CALLED. Display-only, and its absence was a lie on screen:
   * machineNameOf falls back device_name → host → the relay's own machine, and every
   * fleet row's device_name was dropped right here — so a list drawn from ten
   * machines labeled every agent with the one machine the relay pointed at (owner,
   * 2026-08-29: 顯示的 Agent 來源都是 aowl 這是錯的. 我有好幾台機器).
   */
  device_name?: string
}

export const getAttachableAgents = (
  projectId?: string,
  /** Dynamo path only: incremental delivery — see dynamoGetAttachableAgents. The edge
   *  path is a single call to one machine and has nothing to deliver early. */
  opts?: { first?: string | null; onAgents?: (batch: AttachableAgent[]) => void },
) =>
  dynamoGetAttachableAgents(projectId ?? '', opts)
/** Attach an agent. `deviceId` is REQUIRED on the DynamoDB path (the roster row needs the
 *  hosting machine so the feed can reach the agent's pane); the edge path ignores it. */
export const projectAttachAgent = (
  projectId: string,
  channelHash: string,
  label: string,
  deviceId?: string,
) =>
  deviceId
      ? dynamoAttachProjectAgent(projectId, channelHash, deviceId, label)
      : /*
         * REFUSED, never padded. This used to be `deviceId ?? ''`, and the
         * deviceless AGENT# row it wrote was a poison record: the feed Lambda
         * POSTed device_id:"" → BFF 400 → throw → the ordered stream redrove
         * the batch forever and EVERY agent in the conversation went silent
         * (avg-main's diagnosis, 2026-08-19). A row the feed cannot route is
         * not worth writing — say so and name the agent instead.
         */
        Promise.resolve({ ok: false, error: 'no_device_for_agent' })
/** Owner-only dial on an attached agent: speak / listen / mute. */
export const projectAgentMode = (projectId: string, channelHash: string, mode: AgentMode) =>
  dynamoProjectAgentMode(projectId, channelHash, mode)
export const projectDetachAgent = (projectId: string, channelHash: string) =>
  dynamoDetachProjectAgent(projectId, channelHash)
/** Leave a project. Membership lives on the router (authoritative); the local server
 *  proxies and then drops its own attachments for the departed project. An owner may
 *  need to transfer or dissolve — the router's error text says so, show it verbatim. */
export const projectLeave = (projectId: string) =>
  dynamoProjectLeave(projectId)
/* ── project file attachments ─────────────────────────────────────────────── */

/**
 * A file message's `body` is not text — it is a JSON "card":
 *   { "type": "file", "text": "<optional caption>", "attachments": [ … ] }
 * The server strips internal fields (wrapped_cks, chunk_size, chunk_count, sha256)
 * from the UI-facing copy, leaving display meta only.
 */
export type Attachment = {
  file_id: string
  /** sha256[:16] of the file's content — the <chat-file:HASH> tag's key (the CLI
   *  mints both; rides envelope.attachments per avg-main's contract). */
  content_hash?: string
  name?: string
  mime?: string
  size?: number
  w?: number | null
  h?: number | null
  duration_ms?: number | null
  /** Inline base64 thumbnail (≤32KB) when the sender produced one. */
  thumb?: string | null
  sync_state?: string
}

export type FileCard = { type: 'file'; text?: string; attachments?: Attachment[] }

/** Parse a message body as a file card. Returns null for ordinary text. */
export function parseFileCard(body: string | null | undefined): FileCard | null {
  if (!body || !body.includes('"type"')) return null
  try {
    const card: unknown = JSON.parse(body)
    if (card && typeof card === 'object' && (card as FileCard).type === 'file') {
      return card as FileCard
    }
  } catch {
    // Ordinary text that happens to contain `"type"`.
  }
  return null
}

/** Decrypted blob. 409 `not_ready` until the recipient has fetched it. */
/**
 * `httpUrl`, like every other URL handed to the browser to fetch.
 *
 * This was the one blob helper that returned a bare path while `noteFileBlobUrl` next
 * to it returned an absolute one — invisible in the normal build, because `httpUrl`
 * is the identity function while `API_BASE` is empty and the page is served from the
 * same origin as the API.
 *
 * It stops being invisible in a CROSS-ORIGIN build, which is what the native shell
 * is: there `API_BASE` is set, and a bare path resolves against the app's own origin
 * instead of the server. The `<img>` and `<a href>` here, and the audio/video players
 * in the project chat's attachment card, would all reach for a host that has no such
 * route. Same failure the shell's other origin breaks have: fine on a desktop
 * browser, broken only where nobody was looking.
 */
export const projectFileBlobUrl = (projectId: string, fileId: string) =>
  httpUrl(
    `/project/file/blob?project_id=${encodeURIComponent(projectId)}&file_id=${encodeURIComponent(fileId)}`,
  )

/** Retract a shared file: strips the attachment from the card (and drops the message
 *  when it carried nothing else). */
export const projectFileDelete = (projectId: string, messageId: string, fileId: string) =>
  api.post<{ ok?: boolean; error?: string }>('/project/file/delete', {
    project_id: projectId,
    message_id: messageId,
    file_id: fileId,
  })

/**
 * 25 MB, NOT 100 — and it said 100 until 2026-09-02.
 *
 * This mirrored the EDGE's `MAX_FILE_BYTES` (noteFiles.ts, 100 MB), which was the right
 * server to mirror while a project file went through the edge's own chunk lane. M2
 * deleted that lane; a project attachment is now a presigned S3 PUT through the chat
 * service, whose ceiling is `MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024`
 * (avengents-homepage/chat/lambda/attachments.ts) and is enforced at MINT, before any
 * bytes move.
 *
 * So for everything between 25 and 100 MB this check — which exists precisely to give a
 * readable message — silently stopped firing, and the person got the mint's raw
 * `invalid_size` instead. A deletion's consequence, four hundred lines from the
 * deletion, with nothing able to see it: the two numbers live in different repositories.
 *
 * projectFileLimit.test.ts reads the lambda when it is checked out beside this repo and
 * fails if the two drift again.
 */
export const PROJECT_FILE_MAX = 25 * 1024 * 1024

/**
 * Share ONE file into a project. Multipart: the file part plus an optional `text`
 * caption, in either order — Slack-style, one card carries a caption above and the
 * attachment below.
 *
 * One request per file: the server builds a card per upload, so batching would collapse
 * several files into one card. The caller sends the composer text as the caption of the
 * FIRST file only, matching the original.
 */
export async function projectFileUpload(
  projectId: string,
  file: File,
  caption: string,
  onProgress?: (pct: number) => void,
  fromEmail?: string | null,
  fromName?: string | null,
  fromPicture?: string | null,
): Promise<{ ok?: boolean; error?: string; file_id?: string }> {
  /*
   * Presigned S3 upload plus a claiming message. The sender identity rides along so the
   * uploader's own optimistic row shows their name and photo.
   *
   * A second pipeline used to sit under this: the edge's chunked, E2E, per-recipient
   * upload over the relay, reached when the backend flag was off. It went with the edge
   * lane on 2026-09-02. noteFileUpload below is the surviving chunked implementation and
   * is the reference if that shape is ever needed here again.
   */
  return dynamoProjectFileUpload(projectId, file, caption, onProgress, fromEmail, fromName, fromPicture)
}

/* ── git (Source Control) ─────────────────────────────────────────────────── */

/** One entry in a status group. `code` is git's porcelain X/Y letter. */
export type GitFile = { path: string; code: string }

export type GitStatus = {
  repo: string | null
  /** Where the server looked when there is no repo — worth showing in the empty state. */
  start?: string
  branch: string | null
  upstream: string | null
  ahead: number
  behind: number
  detached: boolean
  staged: GitFile[]
  unstaged: GitFile[]
  untracked: string[]
  error: string | null
}

export type GitCommit = {
  hash: string
  short: string
  author: string
  /** Relative, straight from `%ar` — already human-readable, do not reformat. */
  date: string
  subject: string
}

/**
 * Uniform shape of every /git action endpoint. `output` is git's own text.
 *
 * A git-level FAILURE still arrives as HTTP 200 with `ok: false` — popping an empty
 * stash, amending in a repo with no commits, a rejected push. Branch on `ok`, never on
 * the HTTP status or on whether the promise rejected, or every one of those reads as
 * success.
 */
export type GitResult = { ok: boolean; code?: number; output: string }

/**
 * Every git call is scoped by WHERE, and the three parts are not interchangeable:
 *   - `path`  — pin an already-resolved repo root, so the panel keeps working after
 *               the agent `cd`s somewhere else,
 *   - `session` — no path yet: the server resolves the tmux pane's directory, which
 *               is the same rule the file browser uses (tmux_sessions.default_browse_path),
 *   - `host`  — /git/* is in the remote proxy path list, so this routes the whole
 *               call to that mounted remote. Omitting it silently runs git on Origin
 *               against a path that belongs to another machine.
 */
export type GitScope = {
  path?: string | null
  session?: string | null
  host?: string | null
}

function gitQuery(scope: GitScope, extra?: Record<string, string>): string {
  const q = new URLSearchParams()
  if (scope.path) q.set('path', scope.path)
  else if (scope.session) q.set('session', scope.session)
  if (scope.host) q.set('host', scope.host)
  for (const [k, v] of Object.entries(extra ?? {})) q.set(k, v)
  const s = q.toString()
  return s ? `?${s}` : ''
}

/** POST bodies carry `path`; `host` still has to ride in the query for the proxy. */
function gitPost<T>(path: string, scope: GitScope, body: Record<string, unknown>) {
  const q = scope.host ? `?host=${encodeURIComponent(scope.host)}` : ''
  return api.post<T>(`${path}${q}`, { ...body, ...(scope.path ? { path: scope.path } : {}) })
}

export const gitStatus = (scope: GitScope) =>
  api.get<GitStatus>(`/git/status${gitQuery(scope)}`)

export const gitBranches = (scope: GitScope) =>
  api.get<{ repo: string; current: string | null; branches: string[]; error: string | null }>(
    `/git/branches${gitQuery(scope)}`,
  )

export const gitLog = (scope: GitScope, branch?: string, n = 100) =>
  api.get<{ repo: string; branch: string | null; commits: GitCommit[]; error: string | null }>(
    `/git/log${gitQuery(scope, { n: String(n), ...(branch ? { branch } : {}) })}`,
  )

/**
 * `staged` and `untracked` select WHICH diff, and they are mutually exclusive:
 * untracked files have no blob to compare against, so the server runs
 * `diff --no-index /dev/null <file>` to render the whole file as additions.
 */
export const gitDiff = (scope: GitScope, file: string, kind: 'staged' | 'unstaged' | 'untracked') =>
  api.get<{ repo: string; diff: string; error: string | null }>(
    `/git/diff${gitQuery(scope, {
      file,
      ...(kind === 'staged' ? { staged: '1' } : {}),
      ...(kind === 'untracked' ? { untracked: '1' } : {}),
    })}`,
  )

/** Omitting `files` stages/unstages everything — the server treats empty as `all`. */
export const gitStage = (scope: GitScope, files?: string[]) =>
  gitPost<GitResult>('/git/stage', scope, files?.length ? { files } : { all: true })

export const gitUnstage = (scope: GitScope, files?: string[]) =>
  gitPost<GitResult>('/git/unstage', scope, files?.length ? { files } : { all: true })

/**
 * `amend` rewrites the previous commit. An empty `message` with amend is VS Code's
 * "leave the message alone" case and the server maps it to --no-edit, so do NOT
 * default it to something non-empty here or amending would silently retitle commits.
 */
export const gitCommit = (
  scope: GitScope,
  message: string,
  push = false,
  amend = false,
) => gitPost<GitResult>('/git/commit', scope, { message, push, ...(amend ? { amend: true } : {}) })

/**
 * IRREVERSIBLE. Tracked files are restored from the index, untracked ones are
 * DELETED — two different destructions behind one verb, which is why the server
 * reports what it actually did per path instead of a single ok flag. Show that back
 * to the user rather than assuming the request matched the outcome.
 *
 * An empty `files` array is rejected by the server (400) rather than treated as
 * "everything", so callers cannot wipe a work tree through a mistaken empty list.
 */
export type GitDiscardResult = GitResult & {
  restored?: string[]
  deleted?: string[]
  skipped?: { path: string; reason: string }[]
}

export const gitDiscard = (scope: GitScope, files: string[]) =>
  gitPost<GitDiscardResult>('/git/discard', scope, { files })

export type GitStash = { ref: string; subject: string }

export const gitStash = (
  scope: GitScope,
  action: 'push' | 'pop' | 'list',
  opts?: { message?: string; untracked?: boolean },
) =>
  gitPost<GitResult & { stashes?: GitStash[] }>('/git/stash', scope, {
    action,
    ...(opts?.message ? { message: opts.message } : {}),
    ...(opts?.untracked ? { untracked: true } : {}),
  })

/** One commit with its full patch — what a History row opens. */
export type GitShow = {
  repo: string
  hash: string
  short: string
  author: string
  date: string
  subject: string
  body: string
  diff: string
  error: string | null
}

export const gitShow = (scope: GitScope, hash: string) =>
  api.get<GitShow>(`/git/show${gitQuery(scope, { hash })}`)

export const gitPush = (scope: GitScope) => gitPost<GitResult>('/git/push', scope, {})
export const gitPull = (scope: GitScope) => gitPost<GitResult>('/git/pull', scope, {})
export const gitFetch = (scope: GitScope) => gitPost<GitResult>('/git/fetch', scope, {})

export const gitCheckout = (scope: GitScope, branch: string, create = false) =>
  gitPost<GitResult>('/git/checkout', scope, { branch, create })

/* ── notes: the per-agent work queue ──────────────────────────────────────── */

/**
 * A note is a GLOBAL object with a many-to-many attachment to agents, not a row
 * in one agent's list. That shape is forced by the requirement that one note can
 * be dispatched to several agents and appear in each of their terminals.
 *
 * Two consequences worth stating, because both are easy to get backwards:
 *  - `state` and `order` live on the RECEIVER, not on the note. A note executed
 *    by agent A is still pending for agent B, and its position in each queue is
 *    independent. Putting state on the note would make dispatching to one agent
 *    silently remove it from the others.
 *  - Removing a receiver is not one operation but two. `remove` physically drops
 *    a pending receiver (nothing happened, nothing to remember); `detach` marks
 *    an executed one as gone while KEEPING the record, because that record is
 *    the execution history the whole feature exists to accumulate.
 */
export type NoteReceiver = {
  host: string | null
  session: string
  state: 'pending' | 'executed'
  order: number
  executed_ts?: number | null
  /** Set when the agent went away; the entry stays for provenance. */
  detached_ts?: number | null
}

/** Who put a note in the queue. Absent on notes written before agents could
 *  create them — treated as the owner, which is what they were. */
export type NoteOrigin = {
  kind: 'human' | 'agent'
  hash?: string
  label?: string
}

export type Note = {
  id: string
  text: string
  /**
   * Files on this note. The meta fields are byte-identical to a project
   * attachment's, deliberately, so the same readers work — but the mechanism is not
   * shared and must not become shared: notes live in ~/.agent-api/notes.json on this
   * edge and never reach the router, so there are no per-recipient wrapped keys, no
   * chunking and no sync state to track. avengents-server's argument for keeping them
   * apart, which is the one that decided it: every mechanism copied across is a place
   * that can go wrong, and the conditions that make it right — who the recipients
   * are, when keys rotate, whether the chunk count matches — mean nothing here, so
   * nothing would tell you when it did.
   */
  attachments?: NoteAttachment[]
  created_ts: number
  created_by?: NoteOrigin
  updated_ts?: number
  receivers: NoteReceiver[]
  archived: boolean
  archived_ts?: number | null
}

/**
 * A file on a note. Same meta names as a project attachment, and deliberately its own
 * type: the two are not the same thing and must not drift into being treated as one.
 * No `thumb`/`w`/`h` — there is no thumbnail pipeline here — and one field a project
 * attachment can never have.
 */
export type NoteAttachment = {
  file_id: string
  name?: string
  mime?: string
  size?: number
  sha256?: string
  uploaded_ts?: number
  /**
   * Absolute path ON THIS EDGE. What makes a note attachment reachable at all: the
   * agent runs on this machine, so it opens the file rather than fetching it.
   *
   * Meaningless on any other host. avengents-server states this in the module
   * (`PATH_HOST = None`) and does not support remote receivers in v1 — so a note
   * dispatched to an agent with a non-null host must NOT carry these paths.
   */
  path?: string
}

export type NoteRef = { host: string | null; session: string }

/**
 * Normalise a note row into the receivers-array shape.
 *
 * The per-agent GET currently answers with the ASKING agent's state and order
 * inlined and no receivers list — a reasonable projection for "this agent's
 * queue", but not the shape the rest of the client is written against, and one
 * that cannot express "who else is this attached to" at all.
 *
 * Tolerating both here rather than at each call site does two things: a shape
 * change can never again take the whole app down with a `.find` of undefined
 * (it did, once), and when the server starts sending receivers the extra branch
 * simply stops being taken. `mine` supplies the receiver identity that the
 * flattened form leaves implicit.
 */
function normalizeNote(raw: Note & Partial<NoteReceiver>, mine: NoteRef): Note {
  if (Array.isArray(raw.receivers)) return raw
  return {
    ...raw,
    archived: raw.archived ?? false,
    receivers: [
      {
        host: mine.host,
        session: mine.session,
        state: raw.state ?? 'pending',
        order: raw.order ?? 0,
        executed_ts: raw.executed_ts ?? null,
        ...(raw.detached_ts ? { detached_ts: raw.detached_ts } : {}),
      },
    ],
  }
}

/**
 * `device` NAMES THE MACHINE, like the roster fan-out already does.
 *
 * Without it this went to whichever machine the relay currently points at, which is
 * right while somebody is looking at one and wrong the moment a card is opened for an
 * agent on another. The failure is quiet in the worst way: the rail's COUNT comes from
 * /notes/counts across every machine, so the card would say "3 queued" and then list
 * whatever the pointed-at machine happened to have — including nothing at all.
 */
export const getNotes = async (session: string, host?: string | null, device?: string) => {
  const r = await api.get<{ notes: (Note & Partial<NoteReceiver>)[] }>(
    `/notes?session=${encodeURIComponent(session)}${host ? `&host=${encodeURIComponent(host)}` : ''}`,
    device,
  )
  const mine = { host: host ?? null, session }
  return { notes: (r.notes ?? []).map((n) => normalizeNote(n, mine)) }
}

export const getArchivedNotes = () => api.get<{ notes: Note[] }>('/notes/archive')

/**
 * Pending count per queue, keyed `host\0session` (empty host = this machine).
 *
 * One request rather than one per agent, because the point is a glance across
 * the whole roster: an agent can now drop a note into ANOTHER agent's queue, and
 * without this the recipient's count is only visible to someone already looking
 * at that agent — so a note sent from elsewhere sits unseen, which is the one
 * thing this feature cannot afford.
 */
export const getNoteCounts = () => api.get<{ counts: Record<string, number> }>('/notes/counts')

/* ── bookmarks on an agent's conversation (agents/bookmarks/) ──────────────── */

/**
 * One kept message block (owner, 2026-10-31: 每一個 Message Block 下面有一個書簽的按鈕 … 收進書簽裡面). Kept on the AGENT'S
 * machine beside its notes (avengents-server bookmarks.ts), so every device that opens the agent reads the same list:
 * which block (the transcript file and the block's absolute index — what Clean View loads back to), when it was
 * written, who said it, and its words.
 */
export type Bookmark = {
  id: string
  host: string | null
  session: string
  file: string
  index: number
  ts: number | null
  who: string
  kind: 'you' | 'agent' | 'inbound'
  text: string
  created_ts: number
}

/** The agent's bookmarks, the newest made first — asked of the AGENT's machine, never the ambient one. */
export const getBookmarks = (session: string, host: string | null | undefined, device: string | undefined) =>
  api.get<{ bookmarks: Bookmark[] }>(
    `/bookmarks?session=${encodeURIComponent(session)}${host ? `&host=${encodeURIComponent(host)}` : ''}`,
    device,
  )

export const addBookmark = (
  b: Omit<Bookmark, 'id' | 'created_ts' | 'ts'> & { ts?: number | null },
  device: string | undefined,
) => api.post<{ ok: boolean; bookmark: Bookmark }>('/bookmarks/add', b, device)

export const deleteBookmark = (id: string, device: string | undefined) =>
  api.post<{ ok: boolean }>('/bookmarks/delete', { id }, device)

export const addNote = (text: string, receivers: NoteRef[], fileIds?: string[]) =>
  api.post<{ ok: boolean; note: Note }>('/notes/add', {
    text,
    receivers,
    // Ids only. The metadata is the server's — sending its own description back to it
    // would add a step at which the two could disagree, and nothing this client knows
    // about a file is anything the server did not just tell it.
    ...(fileIds?.length ? { file_ids: fileIds } : {}),
  })

/**
 * Tell the edge the user is mid-input, so it holds messages instead of injecting them.
 *
 * The signal only exists HERE. Composer text never reaches the pty until it is sent,
 * and the microphone is a browser state — the server cannot see either, which is what
 * makes this different from `needs_input`: that one it detects, this one it must be
 * told.
 *
 * A HEARTBEAT, not a switch, and the TTL is the whole safety argument. A latch that
 * failed — tab closed, network dropped, this client crashed — would leave an agent
 * silently unreachable with nobody able to see why. A heartbeat that fails resumes
 * delivery a few seconds later. The two failure directions are not comparable: the
 * worst case here is one message interrupting one keystroke.
 *
 * An unrecognised `reason` is REJECTED by the server rather than stored, because the
 * string is shown to the owner as the explanation for why something is waiting — and
 * a reason nobody defined would be rendered as a confident account of something else.
 */
export const postAgentBusy = (session: string, reason: 'typing' | 'speaking', host?: string | null) =>
  api.post<{ ok: boolean; ttl_s?: number }>('/agent/busy', {
    session,
    reason,
    ...(host ? { host } : {}),
  })

/**
 * Nudge every cursor session to RE-DETECT the terminal theme, after the user flips it.
 *
 * Claude Code re-reads the terminal's colours on its own; cursor only re-detects when
 * asked, so a mid-session flip leaves it painting dark text on the new light background
 * until it is restarted. The server types `/sync-theme` into each cursor pane.
 *
 * NEVER PORTED UNTIL NOW, which is worth recording because of how it was found: I went
 * looking for whether MY layer could bypass the server's message queue, concluded it
 * could not, and the reason turned out to be that this feature did not exist here at
 * all. A gap reads exactly like a guard from the calling side — the grep is empty
 * either way. What made it a finding was asking why it was empty.
 *
 * `deferred` is not an error. `/sync-theme` is keystrokes plus Enter, so it carries the
 * same hazard as a message — flipping the theme while an agent sat at a menu ANSWERED
 * that menu — and the server now holds it behind the same block. Replaying it late is
 * safe precisely because the command carries no value: it says "look at your terminal",
 * so a held one reads the CURRENT theme rather than a stale one.
 */
export const syncCursorTheme = () =>
  api.post<{ ok: boolean; synced?: string[]; deferred?: string[] }>('/agent/sync_cursor_theme', {})

/* ── queued agent messages ────────────────────────────────────────────────── */

/**
 * One message waiting for its recipient to stop being busy.
 *
 * A message is INJECTED into the recipient's pane, so delivering one while the agent
 * sits at a prompt used to paste the text into the menu and press Enter — the message
 * vanished AND answered the question, with the sender told `delivered: true`. The
 * server now holds them instead and flushes when the prompt clears.
 */
export type QueuedMessage = {
  id: string
  from_hash: string
  /**
   * Resolved by the server, which holds the registry. NULL when it cannot resolve —
   * deliberately, rather than a guessed name. `from_hash` is always present, so the
   * caller can tell "unnamed" from "named" instead of trusting a plausible label.
   */
  from_label?: string | null
  ts: number
  /** Seconds this has been waiting, server-computed so it needs no clock agreement. */
  waited_s: number
  /**
   * ~80 characters of the message AS FRAMED — it still carries the
   * `<agent-msg:HASH>` wrapper and may be cut mid-tag. Strip it with `parseInbound`
   * before display: the sender is already in `from_label`, so the framing is noise
   * in a narrow panel, and a truncated preview can end without its closing tag.
   */
  /**
   * Whether the preview is a cut of something longer — the SERVER's answer, not a
   * guess from comparing two strings.
   *
   * The first version put the whole body in every list row and compared it to the
   * preview. That worked and cost too much in the one situation it mattered: `body`
   * is uncapped and up to 50 messages are held per recipient, so a full queue was
   * hundreds of KB every five seconds — **most expensive exactly when someone needs
   * to read the queue**. avengents-server offered the swap; the flag is not a weaker
   * version of the old signal but its source, since truncation is something the
   * server does and therefore knows.
   */
  has_more?: boolean
  preview?: string
}

export type AgentQueue = {
  to_host: string | null
  to_session: string
  to_label?: string | null
  /**
   * Why it is still waiting. THREE states, not two: `null` means the server does not
   * know — a remote edge owns its own queue, and this one does not pretend to see it.
   */
  blocked?: boolean | null
  /**
   * WHICH kind of busy, because `blocked: true` renders as one spinner for two
   * different situations. A recipient at a prompt is waiting on another agent to
   * answer; one whose user is typing or speaking is waiting on a person to stop.
   * Those need different reactions, so a boolean would leave the reader guessing
   * which they are looking at.
   */
  /**
   * Why delivery is waiting. Four states, and they need opposite reactions:
   *
   *   at_prompt   the agent is answering a menu — a PERSON has to reply to it
   *   typing      the owner is mid-sentence — nobody should do anything
   *   speaking    the owner is on the mic — same
   *   working     the agent is mid-turn — it clears itself
   *
   * `working` arrived last and is the one the owner actually asked for: "agent busy
   * means everything queues". avengents-server and I had both implemented the
   * SYMPTOM (`at_prompt`, the menu-eats-the-message bug) and called the rule done.
   * A boolean is what let that pass unnoticed — busy and at-a-prompt were the same
   * `true`, so nothing in the type could tell them apart.
   */
  blocked_reason?: 'at_prompt' | 'typing' | 'speaking' | 'working' | null
  /** The same, under the name the edge actually sends (agentQueueRoutes: `blocked_why`). */
  blocked_why?: 'at_prompt' | 'typing' | 'speaking' | 'working' | null
  depth: number
  queued: QueuedMessage[]
}

/**
 * Ask an agent to read the messaging guide.
 *
 * The wording lives on the edge, not here: every client that offers this should ask in
 * the same words, and a second client phrasing it differently is how an agent gets told
 * two different things about one command.
 *
 * `read` comes back false. It only ASKS — the agent still has to run it, and the flag is
 * written by the CLI when the guide prints. A client that treated a 200 here as "done"
 * would re-enable the picker for an agent that still cannot use it.
 */
export const askAgentReadGuide = (session: string, host?: string | null) =>
  api.post<{ ok: boolean; delivered: boolean; read: boolean; detail?: string }>(
    '/agent/ask_read_guide',
    { session, host: host ?? '' },
  )

/** Every agent with messages waiting, so the rail can show who is holding whose mail. */
export const getAgentQueues = () =>
  api.get<{ ok: boolean; total: number; queues: AgentQueue[] }>('/agent/queues')

/**
 * Try to deliver what is held, now. Omit `session` for every queue on the edge.
 *
 * "Try" is the whole contract: it does NOT bypass the block. A message is held because
 * its recipient is mid-keystroke or mid-turn, and writing into that pane is how a
 * keypress gets eaten — the failure the queue exists to prevent. A recipient that is
 * still busy stays queued, and `remaining`/`detail` say so, which is why this returns
 * both instead of a bare ok.
 */
export const releaseAgentQueue = (session?: string, host?: string) =>
  api.post<{ ok: boolean; delivered?: number; remaining?: number; detail?: string }>(
    '/agent/queues/release',
    session ? { session, host: host ?? '' } : {},
  )

/** Drop what is held. Destructive: the sender was told this would arrive, and is not
 *  coming back to check. The edge records each dropped message on the activity bus. */
export const clearAgentQueue = (session?: string, host?: string) =>
  api.post<{ ok: boolean; cleared: number }>(
    '/agent/queues/clear',
    session ? { session, host: host ?? '' } : {},
  )

/**
 * The same two actions, on CHOSEN messages — owner, 2026-09-05: 針對不同的 Agent 以及
 * 不同 Agent 之間傳遞訊息的每一個欄位做出可以選擇要分別單獨 deliver 或是 discard.
 *
 * Each id is tried on its own and answered on its own: `held` names the ones that
 * did not go and why (blocked, gone, held by another machine), so a person who
 * picked three and got one sees which two stayed instead of a count that does not
 * add up.
 *
 * THEIR OWN ROUTES, not `ids` on release/clear: a machine whose edge predates them
 * would read `{ids}` as "no session" and release or CLEAR EVERY QUEUE. On an old
 * edge these answer 404, which the sheet shows as "update that machine".
 */
export const releaseQueuedMessages = (ids: string[]) =>
  api.post<{
    ok: boolean
    delivered: number
    remaining: number
    held: Array<{ id: string; outcome: string; detail: string }>
    detail?: string
  }>('/agent/queues/deliver_ids', { ids })

export const discardQueuedMessages = (ids: string[]) =>
  api.post<{ ok: boolean; cleared: number; missing?: string[] }>('/agent/queues/discard_ids', { ids })

/* ── note attachments ─────────────────────────────────────────────────────── */

/** Same ceiling as a project file. Checked here so a doomed upload is refused before
 *  it is sent rather than after it has finished. */
export const NOTE_FILE_MAX = 100 * 1024 * 1024

/** The bytes of a note attachment. A plain URL: this is an <img src> as often as it
 *  is a download, and both want the browser to do the fetching. */
export const noteFileBlobUrl = (fileId: string) =>
  httpUrl(`/notes/file/blob?file_id=${encodeURIComponent(fileId)}`)

/**
 * Upload ONE file for a note, before the note exists.
 *
 * Upload-then-create, on avengents-server's reasoning: a failure here leaves an
 * unreferenced file, which they sweep in the background, while create-then-upload
 * would leave the owner looking at a note that is visibly missing its attachment.
 * An orphaned file is cheap; an orphaned note is not.
 *
 * Single-shot multipart, no chunking: the file is written next to notes.json on this
 * machine and the recipients are agents running on it.
 */
/** Mirrors the edge's CHUNK_BYTES. A bigger chunk is refused there, not split. */
export const UPLOAD_CHUNK = 512 * 1024

/** base64 without blowing the argument stack on a 512KB chunk. */
export function b64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  let out = ''
  for (let i = 0; i < bytes.length; i += 0x8000) {
    out += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
  }
  return btoa(out)
}

export async function noteFileUpload(
  file: File,
  onProgress?: (pct: number) => void,
): Promise<{
  ok?: boolean
  error?: string
  file_id?: string
  /** The stored file's metadata, and the only place its PATH comes back — the caller
   *  attaching to a terminal agent needs it immediately; there is no note to read it
   *  back from later. */
  meta?: NoteAttachment
}> {
  /*
   * CHUNKED JSON OVER THE RELAY — the backbone changed under this function.
   *
   *   owner, 2026-08-07: 現在基本上所有上傳檔案的功能都跑不通 […] 不是端點的問題，
   *   而是 e2e 的 backbone 都被換掉。原本是 ssh 的架構。現在是 WebSocket 的架構。
   *
   * This was a raw multipart XHR to `httpUrl('/notes/file/upload')`. Under the old
   * architecture the page's origin WAS the edge, so that URL reached a real handler.
   * The portless edge has no port: with API_BASE empty the XHR landed on the vite
   * server, whose 404 guard answered, and every upload in the product failed the same
   * way on the same day. The handler was never broken — it was unreachable.
   *
   * The relay is the only road, and it carries JSON. So the file crosses in 512KB
   * slices through api.post like every other request — same session auth, same vault
   * gate, same `host` routing — and the edge reassembles into the SAME store the
   * multipart path used, so `<note-file:PATH>` tags and dispatch are unchanged.
   * Progress moves per chunk, which is what the XHR's onprogress used to give.
   */
  if (file.size > NOTE_FILE_MAX) {
    throw new Error(`${file.name} is larger than the 100 MB limit`)
  }
  try {
    const begin = await api.post<{ ok: boolean; upload_id: string }>('/notes/file/upload_begin', {
      name: file.name,
      mime: file.type || 'application/octet-stream',
      size: file.size,
    })
    const id = begin.upload_id
    let seq = 0
    for (let o = 0; o < file.size; o += UPLOAD_CHUNK) {
      const buf = await file.slice(o, o + UPLOAD_CHUNK).arrayBuffer()
      await api.post('/notes/file/upload_chunk', { upload_id: id, seq, data_b64: b64(buf) })
      seq += 1
      onProgress?.(Math.round(Math.min(100, ((o + buf.byteLength) / Math.max(1, file.size)) * 100)))
    }
    return await api.post<{ ok: boolean; file_id: string; meta: NoteAttachment }>(
      '/notes/file/upload_finish',
      { upload_id: id },
    )
  } catch (e) {
    /*
     * A 413 here means the two ceilings disagree, not that the file is too big:
     * NOTE_FILE_MAX above already refused everything over the limit, so reaching the
     * server's own check means this client's copy of it is stale. The distinct code
     * exists so this message can say so instead of sending the reader at their file.
     */
    if (e instanceof ApiError && (e.body as { error?: string } | null)?.error === 'file_too_large') {
      const theirs = (e.body as { max_bytes?: number }).max_bytes
      throw new Error(
        `The server refused ${file.name} as too large, but this app checked it against ` +
        formatBytes(NOTE_FILE_MAX) +
        (theirs ? ` and the server's limit is ${formatBytes(theirs)}` : '') +
        '. The two limits disagree — the app needs updating, not the file.',
      )
    }
    throw e
  }
}

export const updateNote = (id: string, text: string) =>
  api.post<{ ok: boolean; note: Note }>('/notes/update', { id, text })

/**
 * Receiver deltas — never a whole-list replace. Two devices adding different
 * agents to the same note merge instead of one silently overwriting the other,
 * which is the failure mode this whole API was shaped to avoid.
 *
 * `detach` is deliberately separate from `remove`: it is what agent-death does,
 * and it is what the UI's ghost cleanup must use. Using `remove` there would
 * physically drop the last receiver and leave the note in a zero-receiver limbo
 * that is neither in any queue nor in the archive — invisible, with no error.
 */
/**
 * A note file's bytes, fetched through the relay in slices and rebuilt as a Blob.
 *
 * `noteFileBlobUrl` still exists but points at a road that no longer goes anywhere —
 * an <img src> URL is a direct HTTP GET, and the portless edge cannot receive one.
 * Same pattern FileViewer already uses for fsContent: base64 in JSON, Blob out.
 */
export async function noteFileBytes(fileId: string, mime?: string): Promise<Blob> {
  const parts: Uint8Array[] = []
  let offset = 0
  let eof = false
  // `while (!eof)`, not `for (;;)` with a break: the loop IS bounded — every pass
  // advances offset and the server says when the file ends — and the no-heartbeat scan
  // rightly treats an unbounded-looking await loop as a poll. Making the bound visible
  // in the syntax beats allowlisting this whole file.
  while (!eof) {
    const r = await api.get<{ data_b64: string; size: number; eof: boolean }>(
      `/notes/file/data?file_id=${encodeURIComponent(fileId)}&offset=${offset}`,
    )
    const bin = atob(r.data_b64)
    const bytes = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
    parts.push(bytes)
    offset += bytes.length
    eof = r.eof
  }
  return new Blob(parts as BlobPart[], mime ? { type: mime } : undefined)
}

/**
 * Project-file bytes over the relay — the same slice loop as noteFileBytes, per
 * project file (owner: 把 project 檔案圖的 chunk lane 也做了). The mime rides in
 * every slice's reply; the first one wins.
 */
export async function projectFileBytes(projectId: string, fileId: string): Promise<Blob> {
  // One presigned GET, no chunk/decrypt loop (managed mode, not E2E). The relay slice
  // loop that used to sit here belonged to the edge lane, deleted 2026-09-02.
  return dynamoAttachmentBytes(projectId, fileId)
}

export const noteReceivers = (
  id: string,
  delta: { add?: NoteRef[]; remove?: NoteRef[]; detach?: NoteRef[] },
) =>
  api.post<{ ok: boolean; note: Note }>('/notes/receivers', {
    id,
    add: delta.add ?? [],
    remove: delta.remove ?? [],
    detach: delta.detach ?? [],
  })

/** Mark one receiver executed, or put it back to pending (dispatched ≠ done). */
/**
 * Push a note into a live receiver's terminal and run it — the live half of the
 * chip's Detach (owner: 让 Notes 里面的 Detach 可以直接进入 Terminal 送出并且执行).
 *
 * A BUSY AGENT QUEUES IT — owner, 2026-09-14: 基本上不該阻擋, 而是要將其 Queue 在
 * Message queue 裡面吧. This answered 409 `busy` until then. `delivered` keeps its
 * exact meaning (it is in the pane and running, so do not retry), and `queued` is
 * the separate, new fact — the note is in the message queue and will run when the
 * agent is free.
 */
export const notesDeliver = (id: string, ref: NoteRef) =>
  api.post<{
    ok?: boolean
    delivered?: boolean
    queued?: boolean
    depth?: number
    error?: string
    detail?: string
  }>('/notes/deliver', { id, host: ref.host, session: ref.session })

export const noteState = (id: string, ref: NoteRef, state: 'pending' | 'executed') =>
  api.post<{ ok: boolean; note: Note }>('/notes/state', { id, ...ref, state })

export const deleteNote = (id: string) => api.post<{ ok: boolean }>('/notes/delete', { id })

export const archiveNote = (id: string, archived: boolean) =>
  api.post<{ ok: boolean; note: Note }>('/notes/archive', { id, archived })

/* ── friends (person ↔ person) — avg-main's BFF contract, /account/friends/* ──
   BFF-served /account routes, so they live in relay.ts alongside the machines list
   and reach the BFF DIRECT (bffJson), not via the edge relay. Re-exported here so
   callers still import them from transport/api like everything else. */
export {
  friendSearch,
  friendRequest,
  friendAccept,
  friendReject,
  friendCancel,
  friendRemove,
  blockAccount,
  getBlocked,
  unblockAccount,
  type BlockedAccount,
  forkAcross,
  forkAcrossProgress,
  type ForkAcrossResult,
  type ForkProgress,
  type ForkStage,
  type ForkLeg,
  getFriends,
  getFriendRequests,
  getGroups,
  createGroup,
  searchGroups,
  getGroup,
  updateGroup,
  inviteToGroup,
  cancelGroupInvite,
  acceptGroup,
  declineGroup,
  askToJoinGroup,
  withdrawGroupAsk,
  answerGroupAsk,
  removeFromGroup,
  leaveGroup,
  openGroupChat,
  openGroupDm,
  befriendInGroup,
  cancelBefriendInGroup,
  getGroupLink,
  joinGroupLink,
  setGroupIcon,
  resetGroupLink,
  type GroupLinkPreview,
  type GroupVisibility,
  type GroupRole,
  type GroupRow,
  type GroupInvitation,
  type GroupPerson,
  type GroupView,
  type GroupFound,
  type FriendRelationship,
  type FriendAccount,
  type Friend,
  type FriendRequest,
} from './relay'

/* ── server identity (display name + QR) ──────────────────────────────────── */

/**
 * Bootstrap config. `name` is the SHARED display name — it lives on the server and is
 * broadcast to every connected UI, so all of them agree; `hostname` is the default it
 * started from; `url` is the canonical login URL, which is exactly what /qr encodes.
 */
export type ServerConfig = {
  hostname: string
  name: string
  url: string
}

export const getConfig = () => api.get<ServerConfig>('/config')

